import { artifacts, deployments, listDeployments, getDeployment, getOmtDeploymentStartBlock, getRpcUrl, getDefaultChain, getDeploymentChains, hasDeployment, isChainSupported, getChains, getOMT } from './chunk-7G6YSIZR.mjs';
export { getChains, getDefaultChain, getDeployment, getDeploymentChains, getOMT, getOMTDeploymentInfo, getOmtDeploymentStartBlock, getRpcUrl, getWrappedTokenNameforChain, hasDeployment, isChainSupported, listDeployments } from './chunk-7G6YSIZR.mjs';
import { localhost, bscTestnet, baseSepolia, sepolia, bsc, arbitrum, base, polygon, optimism, mainnet } from 'viem/chains';
import { keccak256, toBytes, createPublicClient, http, encodeAbiParameters, parseUnits } from 'viem';
import OMTArtifact from '@thema/core/artifacts/OMT.sol/OMT.json';
import TestThemaOracleArtifact from '@thema/core/artifacts/TestThemaOracle.sol/TestThemaOracle.json';
import ERC20Artifact from '@thema/core/artifacts/ERC20.sol/ERC20.json';
import ThemaLPVaultArtifact from '@thema/core/artifacts/ThemaLPVault.sol/ThemaLPVault.json';
import { artifacts as artifacts$1 } from '@thema/core';

// src/ui/types/Domain.ts
var TransactionType = /* @__PURE__ */ ((TransactionType2) => {
  TransactionType2[TransactionType2["MINT"] = 0] = "MINT";
  TransactionType2[TransactionType2["REDEEM"] = 1] = "REDEEM";
  return TransactionType2;
})(TransactionType || {});
var TransferType = /* @__PURE__ */ ((TransferType2) => {
  TransferType2[TransferType2["ERC20"] = 0] = "ERC20";
  TransferType2[TransferType2["ERC20_FEE"] = 1] = "ERC20_FEE";
  TransferType2[TransferType2["OMT_MINT"] = 2] = "OMT_MINT";
  TransferType2[TransferType2["OMT_BURN"] = 3] = "OMT_BURN";
  return TransferType2;
})(TransferType || {});
var FeeReceiverRole = /* @__PURE__ */ ((FeeReceiverRole2) => {
  FeeReceiverRole2[FeeReceiverRole2["TEST"] = 0] = "TEST";
  FeeReceiverRole2[FeeReceiverRole2["RETURNS"] = 1] = "RETURNS";
  FeeReceiverRole2[FeeReceiverRole2["LP_FUND"] = 2] = "LP_FUND";
  FeeReceiverRole2[FeeReceiverRole2["TREASURY"] = 3] = "TREASURY";
  return FeeReceiverRole2;
})(FeeReceiverRole || {});
var TokenInterface = /* @__PURE__ */ ((TokenInterface2) => {
  TokenInterface2["ERC20"] = "ERC20";
  TokenInterface2["BEP20"] = "BEP20";
  return TokenInterface2;
})(TokenInterface || {});
var EnvironmentType = /* @__PURE__ */ ((EnvironmentType2) => {
  EnvironmentType2["MOCK"] = "mock";
  EnvironmentType2["LOCAL"] = "local";
  EnvironmentType2["TESTNET"] = "testnet";
  EnvironmentType2["MAINNET"] = "mainnet";
  return EnvironmentType2;
})(EnvironmentType || {});
var ChainType = /* @__PURE__ */ ((ChainType2) => {
  ChainType2["EVM"] = "evm";
  ChainType2["SOLANA"] = "solana";
  ChainType2["ANVIL"] = "anvil";
  return ChainType2;
})(ChainType || {});
var OracleType = /* @__PURE__ */ ((OracleType2) => {
  OracleType2["TEST"] = "test";
  OracleType2["CHAINLINK"] = "chainlink";
  return OracleType2;
})(OracleType || {});
var FeeModelType = /* @__PURE__ */ ((FeeModelType2) => {
  FeeModelType2["FLAT"] = "flat";
  FeeModelType2["ARBITRAGE"] = "arbitrage";
  FeeModelType2["MOCK"] = "mock";
  return FeeModelType2;
})(FeeModelType || {});

// src/ui/config/chains.ts
var CHAIN_CONFIGS = {
  // Mainnets
  "1": {
    chain: mainnet,
    chainKey: "ethereum",
    environment: "mainnet"
  },
  "10": {
    chain: optimism,
    chainKey: "optimism",
    environment: "mainnet"
  },
  "137": {
    chain: polygon,
    chainKey: "polygon",
    environment: "mainnet"
  },
  "8453": {
    chain: base,
    chainKey: "base",
    environment: "mainnet"
  },
  "42161": {
    chain: arbitrum,
    chainKey: "arbitrum",
    environment: "mainnet"
  },
  "56": {
    chain: bsc,
    chainKey: "bnb",
    environment: "mainnet"
  },
  // Testnets
  "11155111": {
    chain: sepolia,
    chainKey: "sepolia",
    environment: "testnet"
  },
  "84532": {
    chain: baseSepolia,
    chainKey: "baseSepolia",
    environment: "testnet"
  },
  "97": {
    chain: bscTestnet,
    chainKey: "bnbTestnet",
    environment: "testnet"
  },
  // Local
  "31337": {
    chain: localhost,
    chainKey: "anvil",
    environment: "local"
  },
  "999": {
    chain: localhost,
    chainKey: "hypevm",
    environment: "local"
  }
};
Object.fromEntries(
  Object.entries(CHAIN_CONFIGS).map(([id, config]) => [id, config.chainKey])
);
function getChainConfig(chainId) {
  const config = CHAIN_CONFIGS[chainId];
  if (!config) {
    throw new Error(`Unsupported chain ID: ${chainId}. Supported chains: ${Object.keys(CHAIN_CONFIGS).join(", ")}`);
  }
  return config;
}
function getChainName(chainId) {
  return CHAIN_CONFIGS[chainId]?.chainKey || `chain${chainId}`;
}
function getTokenInterface(chainId) {
  const id = typeof chainId === "number" ? chainId : Number(chainId);
  if (id === 56 || id === 97) {
    return "BEP20" /* BEP20 */;
  }
  return "ERC20" /* ERC20 */;
}
function getChainId(chainName) {
  const entry = Object.entries(CHAIN_CONFIGS).find(([_, config]) => config.chainKey === chainName);
  return entry ? entry[0] : void 0;
}
function getChainsByEnvironment(env) {
  return Object.values(CHAIN_CONFIGS).filter((config) => config.environment === env);
}
function isSupportedChain(chainId) {
  return chainId in CHAIN_CONFIGS;
}
function getDefaultRpc(chainId) {
  const config = CHAIN_CONFIGS[chainId];
  return config?.defaultRpc || config?.chain.rpcUrls.default.http[0];
}
function getDefaultRpcUrl(chainId) {
  const config = CHAIN_CONFIGS[chainId];
  if (config?.defaultRpc) {
    return config.defaultRpc;
  }
  const infuraKey = process.env.INFURA_API_KEY || "YOUR_32_CHAR_KEY_HERE";
  const fallbackRpcs = {
    "1": `https://mainnet.infura.io/v3/${infuraKey}`,
    "10": `https://optimism-mainnet.infura.io/v3/${infuraKey}`,
    "137": `https://polygon-mainnet.infura.io/v3/${infuraKey}`,
    "8453": `https://base-mainnet.infura.io/v3/${infuraKey}`,
    // Base mainnet
    "42161": `https://arbitrum-mainnet.infura.io/v3/${infuraKey}`,
    "84532": `https://base-sepolia.infura.io/v3/${infuraKey}`,
    "11155111": `https://sepolia.infura.io/v3/${infuraKey}`,
    "56": "https://bsc-dataseed.binance.org/",
    "97": "https://data-seed-prebsc-1-s1.binance.org:8545",
    // BSC testnet
    "31337": "http://127.0.0.1:8545",
    "999": "http://127.0.0.1:8545"
  };
  return fallbackRpcs[chainId] || "http://localhost:8545";
}
var UNISWAP_V3_CONFIG = {
  ethereum: {
    factory: "0x1F98431c8aD98523631AE4a59f267346ea31F984",
    quoterV2: "0x61fFE014bA17989E743c5F6cB21bF9697530B21e",
    swapRouter: "0xE592427A0AEce92De3Edee1F18E0157C05861564",
    swapRouter02: "0x68b3465833fb72A70ecDF485E0e4C7bD8665Fc45",
    positionManager: "0xC36442b4a4522E871399CD717aBDD847Ab11FE88"
  },
  base: {
    factory: "0x33128a8fC17869897dcE68Ed026d694621f6FDfD",
    quoterV2: "0x3d4e44Eb1374240CE5F1B871ab261CD16335B76a",
    swapRouter: "0x2626664c2603336E57B271c5C0b26F421741e481",
    swapRouter02: "0x2626664c2603336E57B271c5C0b26F421741e481",
    positionManager: "0x03a520b32C04BF3bEEf7BEb72E919cf822Ed34f1"
  },
  baseSepolia: {
    factory: "0x4752ba5DBc23f44D87826276BF6Fd6b1C372aD24",
    quoterV2: "0xC5290058841028F1614F3A6F0F5816cAd0df5E27",
    swapRouter: "0x94cC0AaC535CCDB3C01d6787D6413C739ae12bc4",
    swapRouter02: "0x94cC0AaC535CCDB3C01d6787D6413C739ae12bc4",
    positionManager: "0x27F971cb582BF9E50F397e4d29a5C7A34f11faA2"
  },
  bnb: {
    factory: "0x0BFbCF9fa4f9C56B0F40a671Ad40E0805A091865",
    quoterV2: "0xB048Bbc1Ee6b733FFfCFb9e9CeF7375518e25997",
    swapRouter: "0x1b81D678ffb9C0263b24A97847620C99d213eB14",
    swapRouter02: null,
    positionManager: "0x46A15B0b27311cedF172AB29E4f4766fbE7F4364"
  },
  bnbTestnet: {
    factory: "0x0BFbCF9fa4f9C56B0F40a671Ad40E0805A091865",
    quoterV2: "0xB048Bbc1Ee6b733FFfCFb9e9CeF7375518e25997",
    swapRouter: "0x1b81D678ffb9C0263b24A97847620C99d213eB14",
    swapRouter02: null,
    positionManager: "0x46A15B0b27311cedF172AB29E4f4766fbE7F4364"
  },
  sepolia: {
    factory: "0x0227628f3F023bb0B980b67D528571c95c6DaC1c",
    quoterV2: "0xEd1f6473345F45b75F8179591dd5bA1888cf2FB3",
    swapRouter: "0x3bFA4769FB09eefC5a80d6E87c3B9C650f7Ae48E",
    swapRouter02: "0x3bFA4769FB09eefC5a80d6E87c3B9C650f7Ae48E",
    positionManager: "0xC36442b4a4522E871399CD717aBDD847Ab11FE88"
  },
  anvil: {
    // Anvil local development - typically no Uniswap V3 deployed
    factory: "0x82e01223d51eb87e16a03e24687edf0f294da6f1",
    quoterV2: "0xc351628eb244ec633d5f21fbd6621e1a683b1181",
    swapRouter: "0x7bc06c482dead17c0e297afbc32f6e63d3846650",
    swapRouter02: "0x7bc06c482dead17c0e297afbc32f6e63d3846650",
    positionManager: "0x7969c5ed335650692bc04293b07f5bf2e7a673c0"
  }
};
var CHAINS = {
  ETHEREUM: {
    chainId: "1",
    type: "evm" /* EVM */,
    name: "Ethereum",
    rpcs: /* @__PURE__ */ new Map([
      ["mock" /* MOCK */, ""],
      ["local" /* LOCAL */, ""],
      ["testnet" /* TESTNET */, getDefaultRpcUrl("11155111")],
      ["mainnet" /* MAINNET */, getDefaultRpcUrl("1")]
    ]),
    oracle: {
      type: "chainlink" /* CHAINLINK */,
      priceFeeds: {
        "WETH": "0x5f4eC3Df9cbd43714FE2740f5E3616155c5b8419",
        // ETH/USD
        "WBTC": "0xF4030086522a5bEEa4988F8cA5B36dbC97BeE88c",
        // BTC/USD
        "USDC": "0x8fFfFfd4AfB6115b954Bd326cbe7B4BA576818f6",
        // USDC/USD
        "USDT": "0x3E7d1eAB13ad0104d2750B8863b489D65364e32D"
        // USDT/USD
      }
    }
  },
  OPTIMISM: {
    chainId: "10",
    type: "evm" /* EVM */,
    name: "Optimism",
    rpcs: /* @__PURE__ */ new Map([
      ["mock" /* MOCK */, ""],
      ["local" /* LOCAL */, ""],
      ["testnet" /* TESTNET */, getDefaultRpcUrl("11155111")],
      ["mainnet" /* MAINNET */, getDefaultRpcUrl("1")]
    ]),
    oracle: {
      type: "chainlink" /* CHAINLINK */,
      priceFeeds: {
        "WETH": "0x13e3Ee699D1909E989722E753853AE30b17e08c5",
        // ETH/USD
        "WBTC": "0xD702DD976Fb76Fffc2D3963D037dfDae5b04E593",
        // BTC/USD
        "USDC": "0x16a9FA2FDa030272Ce99B29CF780dFA30361E0f3",
        // USDC/USD
        "USDT": "0xECef79E109e997bCA29c1c0897ec9d7b03647F5E"
        // USDT/USD
      }
    }
  },
  BSC: {
    chainId: "56",
    type: "evm" /* EVM */,
    name: "Binance Smart Chain",
    rpcs: /* @__PURE__ */ new Map([
      ["mock" /* MOCK */, ""],
      ["local" /* LOCAL */, ""],
      ["testnet" /* TESTNET */, getDefaultRpcUrl("11155111")],
      ["mainnet" /* MAINNET */, getDefaultRpcUrl("1")]
    ]),
    oracle: {
      type: "chainlink" /* CHAINLINK */,
      priceFeeds: {
        "WETH": "0x9ef1B8c0E4F7dc8bF5719Ea496883DC6401d5b2e",
        // ETH/USD
        "WBTC": "0x264990fbd0A4796A3E3d8E37C4d5F87a3aCa5Ebf",
        // BTC/USD
        "USDC": "0x51597f405303C4377E36123cBc172b13269EA163",
        // USDC/USD
        "USDT": "0xB97Ad0E74fa7d920791E90258A6E2085088b4320"
        // USDT/USD
      }
    }
  },
  BASE: {
    chainId: "8453",
    type: "evm" /* EVM */,
    name: "Base",
    rpcs: /* @__PURE__ */ new Map([
      ["mock" /* MOCK */, ""],
      ["local" /* LOCAL */, ""],
      ["testnet" /* TESTNET */, getDefaultRpcUrl("84532")],
      ["mainnet" /* MAINNET */, getDefaultRpcUrl("8453")]
    ]),
    oracle: {
      type: "chainlink" /* CHAINLINK */,
      priceFeeds: {
        "WETH": "0x71041dddad3595F9CEd3DcCFBe3D1F4b0a16Bb70",
        // ETH/USD
        "USDC": "0x7e860098F58bBFC8648a4311b374B1D669a2bc6B"
        // USDC/USD
      }
    }
  },
  SOLANA: {
    chainId: "101",
    type: "solana" /* SOLANA */,
    name: "Solana",
    rpcs: /* @__PURE__ */ new Map([
      ["mock" /* MOCK */, ""],
      ["local" /* LOCAL */, ""],
      ["testnet" /* TESTNET */, "https://api.devnet.solana.com"],
      ["mainnet" /* MAINNET */, "https://api.mainnet-beta.solana.com"]
    ]),
    oracle: { type: "test" /* TEST */ }
    // TODO: Add Solana oracle support
  },
  ANVIL: {
    chainId: "31337",
    type: "evm" /* EVM */,
    name: "Anvil",
    rpcs: /* @__PURE__ */ new Map([
      ["mock" /* MOCK */, ""],
      ["local" /* LOCAL */, "http://127.0.0.1:8545"],
      ["testnet" /* TESTNET */, "http://127.0.0.1:8545"],
      ["mainnet" /* MAINNET */, "http://127.0.0.1:8545"]
    ]),
    oracle: {
      type: "chainlink" /* CHAINLINK */,
      priceFeeds: {
        "WETH": "0x5f4eC3Df9cbd43714FE2740f5E3616155c5b8419",
        // ETH/USD
        "WBTC": "0xF4030086522a5bEEa4988F8cA5B36dbC97BeE88c",
        // BTC/USD
        "USDC": "0x8fFfFfd4AfB6115b954Bd326cbe7B4BA576818f6",
        // USDC/USD
        "USDT": "0x3E7d1eAB13ad0104d2750B8863b489D65364e32D"
        // USDT/USD
      }
    }
    // Anvil uses mock oracles for local development
  },
  HYPEVM: {
    chainId: "999",
    type: "evm" /* EVM */,
    name: "Hyperliquid",
    rpcs: /* @__PURE__ */ new Map([
      ["mock" /* MOCK */, ""],
      ["local" /* LOCAL */, ""],
      ["testnet" /* TESTNET */, getDefaultRpcUrl("11155111")],
      ["mainnet" /* MAINNET */, getDefaultRpcUrl("1")]
    ]),
    oracle: { type: "test" /* TEST */ }
    // TODO: Add HypeVM oracle support
  }
};

// src/ui/config/tokens.ts
var TOKEN_CONFIG = {
  ETH: {
    symbol: "ETH",
    name: "Ether",
    decimals: 18,
    initialPrice: "3804.17",
    volatility: 2e-3,
    precision: 2,
    oracles: {
      ethereum: {
        chainlinkFeed: "0x5f4eC3Df9cbd43714FE2740f5E3616155c5b8419",
        description: "ETH/USD"
      },
      base: {
        chainlinkFeed: "0x71041dddad3595F9CEd3DcCFBe3D1F4b0a16Bb70",
        description: "ETH/USD"
      },
      baseSepolia: {
        chainlinkFeed: "0x4aDC67696bA383F43DD60A9e78F2C97Fbbfc7cb1",
        description: "ETH/USD"
      },
      sepolia: {
        chainlinkFeed: "0x694AA1769357215DE4FAC081bf1f309aDC325306",
        description: "ETH/USD"
      },
      anvil: {
        chainlinkFeed: "0x5f4eC3Df9cbd43714FE2740f5E3616155c5b8419",
        description: "ETH/USD"
      }
    }
  },
  WETH: {
    symbol: "WETH",
    name: "Wrapped Ether",
    decimals: 18,
    initialPrice: "3804.17",
    volatility: 2e-3,
    precision: 2,
    addresses: {
      ethereum: "0xC02aaA39b223FE8D0A0e5C4F27eAD9083C756Cc2",
      base: "0x4200000000000000000000000000000000000006",
      baseSepolia: "0x4200000000000000000000000000000000000006",
      sepolia: "0x7b79995e5f793A07Bc00c21412e50Ecae098E7f9",
      anvil: "0xcd8a1c3ba11cf5ecfa6267617243239504a98d90"
    },
    oracles: {
      ethereum: {
        chainlinkFeed: "0x5f4eC3Df9cbd43714FE2740f5E3616155c5b8419",
        description: "ETH/USD"
      },
      base: {
        chainlinkFeed: "0x71041dddad3595F9CEd3DcCFBe3D1F4b0a16Bb70",
        description: "ETH/USD"
      },
      baseSepolia: {
        chainlinkFeed: "0x4aDC67696bA383F43DD60A9e78F2C97Fbbfc7cb1",
        description: "ETH/USD"
      },
      sepolia: {
        chainlinkFeed: "0x694AA1769357215DE4FAC081bf1f309aDC325306",
        description: "ETH/USD"
      },
      anvil: {
        chainlinkFeed: "0x5f4eC3Df9cbd43714FE2740f5E3616155c5b8419",
        description: "ETH/USD"
      }
    }
  },
  BTC: {
    symbol: "BTC",
    name: "Bitcoin",
    decimals: 8,
    initialPrice: "118538.53",
    volatility: 1e-3,
    precision: 2,
    oracles: {
      ethereum: {
        chainlinkFeed: "0xF4030086522a5bEEa4988F8cA5B36dbC97BeE88c",
        description: "BTC/USD"
      },
      baseSepolia: {
        chainlinkFeed: "0x0FB99723Aee6f420beAD13e6bBB79b7E6F034298",
        description: "BTC/USD"
      },
      sepolia: {
        chainlinkFeed: "0x1b44F3514812d835EB1BDB0acB33d3fA3351Ee43",
        description: "BTC/USD"
      },
      anvil: {
        chainlinkFeed: "0xF4030086522a5bEEa4988F8cA5B36dbC97BeE88c",
        description: "BTC/USD"
      }
    }
  },
  WBTC: {
    symbol: "WBTC",
    name: "Wrapped Bitcoin",
    decimals: 8,
    initialPrice: "118538.53",
    volatility: 1e-3,
    precision: 2,
    addresses: {
      ethereum: "0x2260FAC5E5542a773Aa44fBCfeDf7C193bc2C599",
      sepolia: "0x92f3B59a79bFf5dc60c0d59eA13a44D082B2bdFC",
      anvil: "0x0dcd1bf9a1b36ce34237eeafef220932846bcd82"
    },
    oracles: {
      ethereum: {
        chainlinkFeed: "0xF4030086522a5bEEa4988F8cA5B36dbC97BeE88c",
        description: "BTC/USD"
      },
      baseSepolia: {
        chainlinkFeed: "0x0FB99723Aee6f420beAD13e6bBB79b7E6F034298",
        description: "BTC/USD"
      },
      sepolia: {
        chainlinkFeed: "0x1b44F3514812d835EB1BDB0acB33d3fA3351Ee43",
        description: "BTC/USD"
      },
      anvil: {
        chainlinkFeed: "0xCCADC697c55bbB68dc5bCdf8d3CBe83CdD4E071E",
        description: "BTC/USD"
      }
    }
  },
  cbBTC: {
    symbol: "cbBTC",
    name: "Coinbase Bitcoin",
    decimals: 8,
    initialPrice: "95000",
    volatility: 1e-3,
    precision: 2,
    addresses: {
      base: "0xcbB7C0000aB88B473b1f5aFd9ef808440eed33Bf",
      baseSepolia: "0xcbb7c0006f23900c38eb856149f799620fcb8a4a",
      anvil: "0xcbB7C0000aB88B473b1f5aFd9ef808440eed33Bf"
    },
    oracles: {
      base: {
        chainlinkFeed: "0xCCADC697c55bbB68dc5bCdf8d3CBe83CdD4E071E",
        description: "BTC/USD"
      },
      baseSepolia: {
        chainlinkFeed: "0x0FB99723Aee6f420beAD13e6bBB79b7E6F034298",
        description: "BTC/USD"
      },
      anvil: {
        chainlinkFeed: "0xCCADC697c55bbB68dc5bCdf8d3CBe83CdD4E071E",
        description: "BTC/USD"
      }
    }
  },
  WBNB: {
    symbol: "WBNB",
    name: "Wrapped BNB",
    decimals: 18,
    initialPrice: "650",
    volatility: 1e-3,
    precision: 2,
    addresses: {
      bnb: "0xbb4CdB9CBd36B01bD1cBaEBF2De08d9173bc095c",
      bnbTestnet: "0xae13d989daC2f0dEbFf460aC112a837C89BAa7cd"
    },
    oracles: {
      bnb: {
        chainlinkFeed: "0x0567F2323251f0Aab15c8dFb1967E4e8A7D42aeE",
        description: "BNB/USD"
      },
      bnbTestnet: {
        chainlinkFeed: "0x2514895c72f50D8bd4B4F9b1110F0D6bD2c97526",
        description: "BNB/USD"
      }
    }
  },
  BTCB: {
    symbol: "BTCB",
    name: "Binance-Peg Bitcoin",
    decimals: 18,
    initialPrice: "95000",
    volatility: 1e-3,
    precision: 2,
    addresses: {
      bnb: "0x7130d2A12B9BCbFAe4f2634d864A1Ee1Ce3Ead9c",
      bnbTestnet: "0x6ce8dA28E2f864420840cF74474eFf5fD80E65B8"
    },
    oracles: {
      bnb: {
        chainlinkFeed: "0x264990fbd0A4796A3E3d8E37C4d5F87a3aCa5Ebf",
        description: "BTC/USD"
      },
      bnbTestnet: {
        chainlinkFeed: "0x5741306c21795FdCBb9b265Ea0255F499DFe515C",
        description: "BTC/USD"
      }
    }
  },
  SOL: {
    symbol: "SOL",
    name: "Solana",
    decimals: 9,
    initialPrice: "179.27",
    volatility: 4e-3,
    precision: 2,
    oracles: {
      ethereum: {
        chainlinkFeed: "0x4ffC43a60e009B551865A93d232E33Fce9f01507",
        description: "SOL/USD"
      },
      anvil: {
        chainlinkFeed: "0x4ffC43a60e009B551865A93d232E33Fce9f01507",
        description: "SOL/USD"
      }
      // Note: SOL price feed may not be available on Base Sepolia
    }
  },
  WSOL: {
    symbol: "WSOL",
    name: "Wrapped Solana",
    decimals: 9,
    initialPrice: "179.27",
    volatility: 4e-3,
    precision: 2,
    oracles: {
      ethereum: {
        chainlinkFeed: "0x4ffC43a60e009B551865A93d232E33Fce9f01507",
        description: "SOL/USD"
      },
      anvil: {
        chainlinkFeed: "0x4ffC43a60e009B551865A93d232E33Fce9f01507",
        description: "SOL/USD"
      }
      // Note: WSOL price feed may not be available on Base Sepolia
    }
  },
  USDC: {
    symbol: "USDC",
    name: "USD Coin",
    decimals: 6,
    initialPrice: "1",
    volatility: 1e-5,
    precision: 6,
    addresses: {
      ethereum: "0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48",
      base: "0x833589fCD6eDb6E08f4c7C32D4f71b54bdA02913",
      baseSepolia: "0x036CbD53842c5426634e7929541eC2318f3dCF7e",
      sepolia: "0x94a9D9AC8a22534E3FaCa9F4e7F2E2cf85d5E4C8",
      anvil: "0x5fc8d32690cc91d4c39d9d3abcbd16989f875707"
    },
    oracles: {
      ethereum: {
        chainlinkFeed: "0x8fFfFfd4AfB6115b954Bd326cbe7B4BA576818f6",
        description: "USDC/USD"
      },
      base: {
        chainlinkFeed: "0x7e860098F58bBFC8648a4311b374B1D669a2bc6B",
        description: "USDC/USD"
      },
      baseSepolia: {
        chainlinkFeed: "0xd30e2101a97dcbAeBCBC04F14C3f624E67A35165",
        description: "USDC/USD"
      },
      sepolia: {
        chainlinkFeed: "0xA2F78ab2355fe2f984D808B5CeE7FD0A93D5270E",
        description: "USDC/USD"
      },
      anvil: {
        chainlinkFeed: "0x8fFfFfd4AfB6115b954Bd326cbe7B4BA576818f6",
        description: "USDC/USD"
      }
    }
  },
  USDT: {
    symbol: "USDT",
    name: "Tether USD",
    decimals: 6,
    // Default for Ethereum
    initialPrice: "1",
    volatility: 1e-4,
    precision: 6,
    addresses: {
      ethereum: "0xdAC17F958D2ee523a2206206994597C13D831ec7",
      bnb: "0x55d398326f99059fF775485246999027B3197955",
      bnbTestnet: "0x337610d27c682E347C9cD60BD4b3b107C9d34dDd",
      sepolia: "0xaA8E23Fb1079EA71e0a56F48a2aA51851D8433D0",
      anvil: "0xa513e6e4b8f2a923d98304ec87f64353c4d5c853"
    },
    decimalsOverride: {
      bnb: 18,
      // BNB chain uses 18 decimals for USDT
      bnbTestnet: 18
    },
    oracles: {
      ethereum: {
        chainlinkFeed: "0x3E7d1eAB13ad0104d2750B8863b489D65364e32D",
        description: "USDT/USD"
      },
      bnb: {
        chainlinkFeed: "0xB97Ad0E74fa7d920791E90258A6E2085088b4320",
        description: "USDT/USD"
      },
      bnbTestnet: {
        chainlinkFeed: "0xEca2605f0BCF2BA5966372C99837b1F182d3D620",
        description: "USDT/USD"
      },
      baseSepolia: {
        // Using USDC feed as proxy since USDT may not be available
        chainlinkFeed: "0xd30e2101a97dcbAeBCBC04F14C3f624E67A35165",
        description: "USDC/USD (proxy for USDT)"
      },
      sepolia: {
        chainlinkFeed: "0xA2F78ab2355fe2f984D808B5CeE7FD0A93D5270E",
        description: "USDC/USD (proxy for USDT)"
      },
      anvil: {
        chainlinkFeed: "0x8fFfFfd4AfB6115b954Bd326cbe7B4BA576818f6",
        description: "USDC/USD"
      }
    }
  },
  DAI: {
    symbol: "DAI",
    name: "Dai Stablecoin",
    decimals: 18,
    initialPrice: "1",
    volatility: 1e-4,
    precision: 6,
    addresses: {
      ethereum: "0x6B175474E89094C44Da98b954EedeAC495271d0F",
      sepolia: "0x3e622317f8C93f7328350cF0B56d9eD4C620C5d6",
      anvil: "0x6B175474E89094C44Da98b954EedeAC495271d0F"
    },
    oracles: {
      ethereum: {
        chainlinkFeed: "0xAed0c38402a5d19df6E4c03F4E2DceD6e29c1ee9",
        description: "DAI/USD"
      },
      baseSepolia: {
        chainlinkFeed: "0xD1092a65338d049DB68D7Be6bD89d17a0929945e",
        description: "DAI/USD"
      },
      sepolia: {
        chainlinkFeed: "0x14866185B1962B63C3Ea9E03Bc1da838bab34C19",
        description: "DAI/USD"
      },
      anvil: {
        chainlinkFeed: "0xAed0c38402a5d19df6E4c03F4E2DceD6e29c1ee9",
        description: "DAI/USD"
      }
    }
  },
  USDE: {
    symbol: "USDE",
    name: "Ethena USDe",
    decimals: 18,
    initialPrice: "1",
    volatility: 1e-4,
    precision: 6,
    addresses: {
      ethereum: "0x4c9EDD5852cd905f086C759E8383e09bff1E68B3",
      anvil: "0x4c9EDD5852cd905f086C759E8383e09bff1E68B3"
    },
    oracles: {
      // Using USDC feed as proxy for USDE (stablecoin)
      ethereum: {
        chainlinkFeed: "0x8fFfFfd4AfB6115b954Bd326cbe7B4BA576818f6",
        description: "USDC/USD (proxy for USDE)"
      }
    }
  },
  // ============ TEST/FAKE TOKENS FOR DEMO DEPLOYMENTS ============
  // These are mock tokens used only in test environments
  // ETH Ecosystem tokens (mock versions for testing)
  LINK: {
    symbol: "LINK",
    name: "Chainlink (Test)",
    decimals: 18,
    initialPrice: "12.90",
    volatility: 2e-3,
    precision: 2,
    oracles: {}
  },
  LDO: {
    symbol: "LDO",
    name: "Lido DAO (Test)",
    decimals: 18,
    initialPrice: "0.830",
    volatility: 2e-3,
    precision: 6,
    oracles: {}
  },
  GRT: {
    symbol: "GRT",
    name: "The Graph (Test)",
    decimals: 18,
    initialPrice: "0.08",
    volatility: 2e-3,
    precision: 6,
    oracles: {}
  },
  MKR: {
    symbol: "MKR",
    name: "Maker (Test)",
    decimals: 18,
    initialPrice: "1268",
    volatility: 2e-3,
    precision: 2,
    oracles: {}
  },
  APE: {
    symbol: "APE",
    name: "ApeCoin (Test)",
    decimals: 18,
    initialPrice: "0.441",
    volatility: 4e-3,
    precision: 6,
    oracles: {}
  },
  // DeFi tokens (mock versions for testing)
  AAVE: {
    symbol: "AAVE",
    name: "AAVE (Test)",
    decimals: 18,
    initialPrice: "151.80",
    volatility: 4e-3,
    precision: 2,
    oracles: {}
  },
  COMP: {
    symbol: "COMP",
    name: "Compound (Test)",
    decimals: 18,
    initialPrice: "46.80",
    volatility: 1e-3,
    precision: 2,
    oracles: {}
  },
  CRV: {
    symbol: "CRV",
    name: "Curve (Test)",
    decimals: 18,
    initialPrice: "0.5178",
    volatility: 1e-3,
    precision: 6,
    oracles: {}
  },
  SUSHI: {
    symbol: "SUSHI",
    name: "Sushi (Test)",
    decimals: 18,
    initialPrice: "0.563",
    volatility: 1e-3,
    precision: 6,
    oracles: {}
  },
  UNI: {
    symbol: "UNI",
    name: "Uniswap (Test)",
    decimals: 18,
    initialPrice: "5.824",
    volatility: 1e-3,
    precision: 6,
    oracles: {}
  },
  BAL: {
    symbol: "BAL",
    name: "Balancer (Test)",
    decimals: 18,
    initialPrice: "1.24",
    volatility: 1e-3,
    precision: 6,
    oracles: {}
  },
  SNX: {
    symbol: "SNX",
    name: "Synthetix (Test)",
    decimals: 18,
    initialPrice: "0.652",
    volatility: 1e-3,
    precision: 6,
    oracles: {}
  },
  // Mock S&P10 tokens (fake tokenized stocks for testing)
  AAPL: {
    symbol: "AAPL",
    name: "Apple (Test Token)",
    decimals: 18,
    initialPrice: "225",
    volatility: 1e-3,
    precision: 2,
    oracles: {}
  },
  NVDA: {
    symbol: "NVDA",
    name: "NVIDIA (Test Token)",
    decimals: 18,
    initialPrice: "120",
    volatility: 0.01,
    precision: 2,
    oracles: {}
  },
  MSFT: {
    symbol: "MSFT",
    name: "Microsoft (Test Token)",
    decimals: 18,
    initialPrice: "394",
    volatility: 1e-3,
    precision: 2,
    oracles: {}
  },
  AMZN: {
    symbol: "AMZN",
    name: "Amazon (Test Token)",
    decimals: 18,
    initialPrice: "204.80",
    volatility: 0.01,
    precision: 2,
    oracles: {}
  },
  META: {
    symbol: "META",
    name: "Meta (Test Token)",
    decimals: 18,
    initialPrice: "627.20",
    volatility: 1e-3,
    precision: 2,
    oracles: {}
  },
  GOOGL: {
    symbol: "GOOGL",
    name: "Alphabet A (Test Token)",
    decimals: 18,
    initialPrice: "169.41",
    volatility: 1e-3,
    precision: 2,
    oracles: {}
  },
  AVGO: {
    symbol: "AVGO",
    name: "Broadcom (Test Token)",
    decimals: 18,
    initialPrice: "190",
    volatility: 1e-3,
    precision: 2,
    oracles: {}
  },
  TSLA: {
    symbol: "TSLA",
    name: "Tesla (Test Token)",
    decimals: 18,
    initialPrice: "278",
    volatility: 0.02,
    precision: 2,
    oracles: {}
  },
  GOOG: {
    symbol: "GOOG",
    name: "Alphabet C (Test Token)",
    decimals: 18,
    initialPrice: "171.67",
    volatility: 1e-3,
    precision: 2,
    oracles: {}
  },
  "BRK.B": {
    symbol: "BRK.B",
    name: "Berkshire B (Test Token)",
    decimals: 18,
    initialPrice: "527.28",
    volatility: 1e-3,
    precision: 2,
    oracles: {}
  }
};
function getTokenAddress(symbol, chain) {
  const token = TOKEN_CONFIG[symbol];
  if (!token || !token.addresses) return void 0;
  return token.addresses[chain];
}
function getUniswapConfig(chain) {
  const config = UNISWAP_V3_CONFIG[chain];
  if (!config || !config.swapRouter || !config.quoterV2 || !config.factory) {
    throw new Error(`Uniswap V3 not supported on chain '${chain}' for environment 'mainnet'`);
  }
  return config;
}

// src/ui/WebClient.ts
var WebClient = class {
  /**
   * List all available deployments
   */
  listDeployments() {
    return listDeployments();
  }
  /**
   * Get a specific deployment by ID
   */
  getDeployment(deploymentId) {
    return getDeployment(deploymentId);
  }
  /**
   * Get metadata for a deployment
   */
  getDeploymentMetadata(deploymentId) {
    const deployment = getDeployment(deploymentId);
    if (!deployment) return void 0;
    return {
      id: deploymentId,
      chains: getDeploymentChains(deploymentId),
      defaultChain: getDefaultChain(deploymentId),
      rpcUrl: getRpcUrl(deploymentId),
      omtDeploymentBlock: getOmtDeploymentStartBlock(deploymentId)
    };
  }
  /**
   * Check if a deployment exists
   */
  hasDeployment(deploymentId) {
    return hasDeployment(deploymentId);
  }
  /**
   * Check if a chain is supported by a deployment
   */
  isChainSupported(deploymentId, chainId) {
    return isChainSupported(deploymentId, chainId);
  }
  /**
   * Get all unique chain IDs across all deployments
   */
  listChains() {
    return getChains();
  }
  /**
   * Get chains for a specific deployment
   */
  getDeploymentChains(deploymentId) {
    return getDeploymentChains(deploymentId);
  }
  /**
   * Get chain configuration
   */
  getChainConfig(chainId) {
    return getChainConfig(chainId);
  }
  /**
   * Get chains by environment
   */
  getChainsByEnvironment(environment) {
    return getChainsByEnvironment(environment);
  }
  /**
   * Get all chain configurations
   */
  getAllChainConfigs() {
    return CHAIN_CONFIGS;
  }
  /**
   * Get token address for a specific chain
   */
  getTokenAddress(symbol, chainId) {
    return getTokenAddress(symbol, chainId);
  }
  /**
   * Get Uniswap configuration for a chain
   */
  getUniswapConfig(chainId) {
    return getUniswapConfig(chainId);
  }
  /**
   * Get all token configurations
   */
  getAllTokenConfigs() {
    return TOKEN_CONFIG;
  }
  /**
   * Get RPC URL for a deployment
   */
  getRpcUrl(deploymentId, chainId) {
    return getRpcUrl(deploymentId, chainId);
  }
  /**
   * Get OMT details by chain and address.
   */
  async getOMT(chainId, address) {
    return getOMT(chainId, address);
  }
};
function createWebClient() {
  return new WebClient();
}
var artifacts2 = {
  OMT: OMTArtifact,
  TestThemaOracle: TestThemaOracleArtifact,
  ERC20: ERC20Artifact,
  ThemaLPVault: ThemaLPVaultArtifact
};

// src/ui/BrowserThemaClient.ts
function fromBigInt(value, decimals = 18) {
  const divisor = BigInt(10) ** BigInt(decimals);
  const wholePart = value / divisor;
  const fractionalPart = value % divisor;
  if (fractionalPart === 0n) {
    return wholePart.toString();
  }
  const fractionalStr = fractionalPart.toString().padStart(decimals, "0");
  const trimmedFractional = fractionalStr.replace(/0+$/, "");
  return trimmedFractional ? `${wholePart}.${trimmedFractional}` : wholePart.toString();
}
var BrowserThemaClient = class {
  constructor(config) {
    this.chainId = config.chainId;
    this.deployment = config.deployment;
    const chainConfig = getChainConfig(config.chainId);
    let rpcUrl = config.rpcUrl;
    if (!rpcUrl && this.deployment?.chainDeployments[config.chainId]?.rpcUrl) {
      rpcUrl = this.deployment.chainDeployments[config.chainId].rpcUrl;
    }
    if (!rpcUrl && chainConfig.chain.rpcUrls?.default?.http?.[0]) {
      rpcUrl = chainConfig.chain.rpcUrls.default.http[0];
    }
    if (!rpcUrl) {
      throw new Error(`No RPC URL available for chain ${config.chainId}`);
    }
    this.publicClient = createPublicClient({
      chain: chainConfig.chain,
      transport: http(rpcUrl)
    });
  }
  /**
   * Get the public client instance
   */
  getPublicClient() {
    return this.publicClient;
  }
  /**
   * Get chain ID
   */
  getChainId() {
    return this.chainId;
  }
  /**
   * Read OMT contract data from blockchain
   */
  async getOMT(chainId, address) {
    if (chainId !== this.chainId) {
      throw new Error(`Chain mismatch: client is for chain ${this.chainId}, requested ${chainId}`);
    }
    try {
      const omtAbi = artifacts2.OMT.abi;
      const [name, symbol, totalSupply, tokenInfos, owner, feeModel, oracle, description] = await Promise.all([
        this.publicClient.readContract({
          address,
          abi: omtAbi,
          functionName: "name"
        }),
        this.publicClient.readContract({
          address,
          abi: omtAbi,
          functionName: "symbol"
        }),
        this.publicClient.readContract({
          address,
          abi: omtAbi,
          functionName: "totalSupply"
        }),
        this.publicClient.readContract({
          address,
          abi: omtAbi,
          functionName: "getTokens"
        }),
        this.publicClient.readContract({
          address,
          abi: omtAbi,
          functionName: "owner"
        }).catch(() => void 0),
        this.publicClient.readContract({
          address,
          abi: omtAbi,
          functionName: "feeModel"
        }).catch(() => void 0),
        this.publicClient.readContract({
          address,
          abi: omtAbi,
          functionName: "oracle"
        }).catch(() => void 0),
        this.publicClient.readContract({
          address,
          abi: omtAbi,
          functionName: "description"
        }).catch(() => "")
      ]);
      const components = tokenInfos.map((tokenInfo) => ({
        token: {
          address: tokenInfo.addr,
          symbol: tokenInfo.symbol,
          name: tokenInfo.symbol,
          // Use symbol as name if not available
          decimals: Number(tokenInfo.decimals)
        },
        quantity: fromBigInt(tokenInfo.quantity, 18)
        // Quantity is always 18 decimals per OMT spec
      }));
      return {
        address,
        chainId,
        name,
        symbol,
        description: description || "",
        totalSupply: fromBigInt(totalSupply, 18),
        // OMT supply is always 18 decimals
        decimals: 18,
        components,
        owner,
        feeModel,
        oracle,
        status: "active",
        validFrom: 0,
        isController: false
      };
    } catch (error) {
      console.error(`Failed to fetch OMT data for ${address}:`, error);
      throw new Error(`Could not fetch OMT at address ${address}: ${error instanceof Error ? error.message : String(error)}`);
    }
  }
  /**
   * Calculate NAV (Net Asset Value) for an OMT
   */
  async getNAV(chainId, address) {
    if (chainId !== this.chainId) {
      throw new Error(`Chain mismatch: client is for chain ${this.chainId}, requested ${chainId}`);
    }
    try {
      const omtAbi = artifacts2.OMT.abi;
      const nav = await this.publicClient.readContract({
        address,
        abi: omtAbi,
        functionName: "calculateNav",
        args: []
      });
      return fromBigInt(nav, 18);
    } catch (error) {
      throw new Error(`Failed to calculate NAV for OMT ${address}: ${error instanceof Error ? error.message : String(error)}`);
    }
  }
  /**
   * Get price for a token from the oracle
   */
  async getPrice(chainId, tokenAddress, oracleAddress) {
    if (chainId !== this.chainId) {
      throw new Error(`Chain mismatch: client is for chain ${this.chainId}, requested ${chainId}`);
    }
    let oracle = oracleAddress;
    if (!oracle && this.deployment?.chainDeployments[chainId]) {
      oracle = this.deployment.chainDeployments[chainId].oracleAddress;
    }
    if (!oracle) {
      throw new Error(`No oracle address available for chain ${chainId}`);
    }
    try {
      const oracleAbi = artifacts2.TestThemaOracle.abi;
      const price = await this.publicClient.readContract({
        address: oracle,
        abi: oracleAbi,
        functionName: "getPrice",
        args: [tokenAddress]
      });
      return fromBigInt(price, 18);
    } catch (error) {
      return "1.0";
    }
  }
  /**
   * Get total supply for an OMT
   */
  async getTotalSupply(chainId, address) {
    if (chainId !== this.chainId) {
      throw new Error(`Chain mismatch: client is for chain ${this.chainId}, requested ${chainId}`);
    }
    try {
      const omtAbi = artifacts2.OMT.abi;
      const totalSupply = await this.publicClient.readContract({
        address,
        abi: omtAbi,
        functionName: "totalSupply"
      });
      return fromBigInt(totalSupply, 18);
    } catch (error) {
      throw new Error(`Failed to get total supply for OMT ${address}: ${error instanceof Error ? error.message : String(error)}`);
    }
  }
  /**
   * Get decimals for an OMT (always 18 for OMT tokens)
   */
  async getDecimals(chainId, address) {
    if (chainId !== this.chainId) {
      throw new Error(`Chain mismatch: client is for chain ${this.chainId}, requested ${chainId}`);
    }
    return 18;
  }
};
function createBrowserClient(config) {
  return new BrowserThemaClient(config);
}

// src/ui/config/explorers.ts
var BLOCK_EXPLORERS = {
  // Mainnet
  "1": {
    name: "Etherscan",
    url: "https://etherscan.io"
  },
  "8453": {
    name: "Basescan",
    url: "https://basescan.org"
  },
  "42161": {
    name: "Arbiscan",
    url: "https://arbiscan.io"
  },
  "10": {
    name: "Optimistic Etherscan",
    url: "https://optimistic.etherscan.io"
  },
  "137": {
    name: "Polygonscan",
    url: "https://polygonscan.com"
  },
  // Testnets
  "11155111": {
    name: "Sepolia Etherscan",
    url: "https://sepolia.etherscan.io"
  },
  "84532": {
    name: "Base Sepolia",
    url: "https://sepolia.basescan.org"
  },
  "421614": {
    name: "Arbitrum Sepolia",
    url: "https://sepolia.arbiscan.io"
  },
  "11155420": {
    name: "Optimism Sepolia",
    url: "https://sepolia-optimism.etherscan.io"
  },
  "80001": {
    name: "Mumbai Polygonscan",
    url: "https://mumbai.polygonscan.com"
  },
  // Local
  "31337": {
    name: "Local",
    url: ""
    // No explorer for local chains
  }
};
function getBlockExplorerUrl(chainId, address) {
  const explorer = BLOCK_EXPLORERS[chainId];
  if (!explorer || !explorer.url) {
    return "";
  }
  return `${explorer.url}/address/${address}`;
}

// src/ui/vault/vaultConfig.ts
var CHAIN_DISPLAY_NAMES = {
  "1": "Ethereum",
  "10": "Optimism",
  "137": "Polygon",
  "8453": "Base",
  "42161": "Arbitrum",
  "56": "BNB",
  "11155111": "Sepolia",
  "84532": "Base Sepolia",
  "97": "BNB Testnet",
  "31337": "Anvil"
};
function displayChainName(chainId) {
  return CHAIN_DISPLAY_NAMES[chainId] ?? `Chain ${chainId}`;
}
function isAddress(v) {
  return !!v && /^0x[a-fA-F0-9]{40}$/.test(v);
}
function deriveVaults() {
  const out = [];
  for (const deployment of deployments) {
    const userByName = /* @__PURE__ */ new Map();
    for (const u of deployment.users) {
      if (isAddress(u.address)) userByName.set(u.name, u.address);
    }
    for (const [chainId, chainDeployment] of Object.entries(deployment.chainDeployments)) {
      for (const omt of Object.values(chainDeployment.omts)) {
        for (const [handle, vault] of Object.entries(omt.vaults)) {
          if (vault.kind !== "lp") continue;
          if (!isAddress(vault.address)) continue;
          const assetMeta = vault.stablecoin ?? vault.accountingBase;
          if (!assetMeta) continue;
          const accountingMeta = vault.accountingBase ?? assetMeta;
          const adminHandles = (vault.roles?.admin ?? []).map((h) => {
            const bareName = h.startsWith("@") ? h.slice(1) : h;
            return { handle: h, address: userByName.get(bareName) };
          });
          const migrationSources = [];
          for (const src of Object.values(vault.migration?.sources ?? {})) {
            if (!isAddress(src.address)) continue;
            migrationSources.push({
              chainId: src.chainId ?? chainId,
              address: src.address,
              exchangeRate: src.exchangeRate
            });
          }
          out.push({
            omtSymbol: omt.symbol,
            omtName: omt.name ?? omt.symbol,
            vaultAddress: vault.address,
            chainId,
            chainName: displayChainName(chainId),
            rpcUrl: chainDeployment.rpcUrl,
            assetSymbol: assetMeta.symbol,
            assetDecimals: assetMeta.decimals,
            accountingSymbol: accountingMeta.symbol,
            accountingDecimals: accountingMeta.decimals,
            label: vault.label ?? vault.name ?? `${omt.symbol} LP Vault`,
            cueHandle: handle,
            enabled: vault.enabled !== false,
            lifecycle: vault.lifecycle ?? "active",
            adminHandles,
            isMigrationTarget: (vault.migration?.shareCap ?? 0) > 0,
            migrationSources
          });
        }
      }
    }
  }
  const lifecycleRank = {
    active: 0,
    deprecated: 1,
    legacy: 2,
    destroyed: 3
  };
  out.sort((a, b) => {
    if (a.enabled !== b.enabled) return a.enabled ? -1 : 1;
    if (a.lifecycle !== b.lifecycle) return lifecycleRank[a.lifecycle] - lifecycleRank[b.lifecycle];
    if (a.chainId !== b.chainId) return a.chainId.localeCompare(b.chainId);
    return a.label.localeCompare(b.label);
  });
  return out;
}
var VAULT_CONFIGS = deriveVaults();
function getVaultForOmt(omtSymbol) {
  return VAULT_CONFIGS.find(
    (v) => v.omtSymbol.toLowerCase() === omtSymbol.toLowerCase()
  );
}
function getVaultByAddress(vaultAddress, chainId) {
  const lc = vaultAddress.toLowerCase();
  return VAULT_CONFIGS.find(
    (v) => v.vaultAddress.toLowerCase() === lc && (chainId === void 0 || v.chainId === chainId)
  );
}
function getVaultMast(vault) {
  const out = [];
  const seen = /* @__PURE__ */ new Set();
  const push = (chainId, address) => {
    const addr = address.toLowerCase();
    const key = `${chainId}_${addr}`;
    if (seen.has(key)) return;
    seen.add(key);
    out.push({ chainId, address: addr });
  };
  push(vault.chainId, vault.vaultAddress);
  for (const src of vault.migrationSources) push(src.chainId, src.address);
  return out;
}
var ACKNOWLEDGEMENT_STATEMENTS = [
  "I understand the vault and Safe infrastructure are experimental.",
  "I understand the vault and Safe contracts are not yet audited.",
  "I understand I may lose all deposited funds.",
  "I understand participation is entirely at my own risk.",
  "I understand this system is currently in trial phase."
];
function computeAcknowledgementsHash(statements = ACKNOWLEDGEMENT_STATEMENTS) {
  const perStatement = statements.map((s) => keccak256(toBytes(s)));
  const encoded = encodeAbiParameters(
    [{ type: "bytes32[]" }],
    [perStatement]
  );
  return keccak256(encoded);
}
var VAULT_ABI = artifacts.ThemaLPVault.abi;
var ERC20_ABI = artifacts.ERC20.abi;
var DEFAULT_ADMIN_ROLE = "0x0000000000000000000000000000000000000000000000000000000000000000";
var ADMIN_ROLE = keccak256(toBytes("ADMIN_ROLE"));
function fromWei(value, decimals = 18) {
  if (value === 0n) return "0";
  const divisor = 10n ** BigInt(decimals);
  const whole = value / divisor;
  const frac = value % divisor;
  if (frac === 0n) return whole.toString();
  const fracStr = frac.toString().padStart(decimals, "0").replace(/0+$/, "");
  return `${whole}.${fracStr}`;
}
var MAX_UINT256 = 2n ** 256n - 1n;
function fmtCap(value, decimals) {
  return value === MAX_UINT256 ? "Unlimited" : fromWei(value, decimals);
}
async function getVaultAssetMetadata(publicClient, vaultAddress) {
  const [assetAddr, accountingAddr] = await publicClient.multicall({
    contracts: [
      { address: vaultAddress, abi: VAULT_ABI, functionName: "asset" },
      { address: vaultAddress, abi: VAULT_ABI, functionName: "accountingBase" }
    ],
    allowFailure: false
  });
  const sameToken = assetAddr.toLowerCase() === accountingAddr.toLowerCase();
  if (sameToken) {
    const [symbol, decimals] = await publicClient.multicall({
      contracts: [
        { address: assetAddr, abi: ERC20_ABI, functionName: "symbol" },
        { address: assetAddr, abi: ERC20_ABI, functionName: "decimals" }
      ],
      allowFailure: false
    });
    const token = {
      address: assetAddr,
      symbol,
      decimals: Number(decimals)
    };
    return { asset: token, accountingBase: token, sameToken: true };
  }
  const [assetSymbol, assetDecimals, accountingSymbol, accountingDecimals] = await publicClient.multicall({
    contracts: [
      { address: assetAddr, abi: ERC20_ABI, functionName: "symbol" },
      { address: assetAddr, abi: ERC20_ABI, functionName: "decimals" },
      { address: accountingAddr, abi: ERC20_ABI, functionName: "symbol" },
      { address: accountingAddr, abi: ERC20_ABI, functionName: "decimals" }
    ],
    allowFailure: false
  });
  return {
    asset: {
      address: assetAddr,
      symbol: assetSymbol,
      decimals: Number(assetDecimals)
    },
    accountingBase: {
      address: accountingAddr,
      symbol: accountingSymbol,
      decimals: Number(accountingDecimals)
    },
    sameToken: false
  };
}
async function getVaultState(publicClient, vaultAddress, assetDecimals = 6) {
  const shareDecimals = assetDecimals + 6;
  const oneShare = 10n ** BigInt(shareDecimals);
  const c = (functionName, args) => ({
    address: vaultAddress,
    abi: VAULT_ABI,
    functionName,
    ...{}
  });
  const results = await publicClient.multicall({
    contracts: [
      c("totalAssets"),
      // 0
      c("totalSupply"),
      // 1
      c("depositCap"),
      // 2
      c("defaultWalletCap"),
      // 3
      c("currentEpochId"),
      // 4
      c("nextRequestId"),
      // 5
      c("lastSettledRequestId"),
      // 6
      c("totalPendingShares"),
      // 7
      c("totalClaimableAssets"),
      // 8
      c("asset"),
      // 9
      c("settler"),
      // 10
      c("safe"),
      // 11
      c("paused")
      // 12
    ],
    allowFailure: true
  });
  const [
    rTotalAssets,
    rTotalSupply,
    rDepositCap,
    rDefaultWalletCap,
    rCurrentEpochId,
    rNextRequestId,
    rLastSettledRequestId,
    rTotalPendingShares,
    rTotalClaimableAssets,
    rAsset,
    rSettler,
    rSafe,
    rPaused
  ] = results;
  const criticals = [rTotalAssets, rAsset, rSafe];
  for (const r of criticals) {
    if (r.status === "failure") {
      throw new Error(
        `Vault read failed: ${r.error instanceof Error ? r.error.message : String(r.error)}`
      );
    }
  }
  const totalAssets = rTotalAssets.result;
  const totalSupply = rTotalSupply.status === "success" ? rTotalSupply.result : 0n;
  const sharePriceWei = totalSupply > 0n ? totalAssets * oneShare / totalSupply : 0n;
  const depositCap = rDepositCap.status === "success" ? rDepositCap.result : 0n;
  const remaining = depositCap > 0n && depositCap !== MAX_UINT256 && depositCap > totalAssets ? depositCap - totalAssets : 0n;
  return {
    totalAssets: fromWei(totalAssets, assetDecimals),
    totalSupply: fromWei(totalSupply, shareDecimals),
    sharePrice: fromWei(sharePriceWei, assetDecimals),
    depositCap: fmtCap(depositCap, assetDecimals),
    remainingCapacity: fmtCap(remaining, assetDecimals),
    currentEpochId: Number(rCurrentEpochId.status === "success" ? rCurrentEpochId.result : 0n),
    nextRequestId: Number(rNextRequestId.status === "success" ? rNextRequestId.result : 0n),
    lastSettledRequestId: Number(rLastSettledRequestId.status === "success" ? rLastSettledRequestId.result : 0n),
    totalPendingShares: fromWei(rTotalPendingShares.status === "success" ? rTotalPendingShares.result : 0n, shareDecimals),
    totalClaimableAssets: fromWei(rTotalClaimableAssets.status === "success" ? rTotalClaimableAssets.result : 0n, assetDecimals),
    defaultWalletCap: fmtCap(rDefaultWalletCap.status === "success" ? rDefaultWalletCap.result : MAX_UINT256, assetDecimals),
    asset: rAsset.result,
    settler: rSettler.status === "success" ? rSettler.result : "0x0000000000000000000000000000000000000000",
    safe: rSafe.result,
    paused: rPaused.status === "success" ? rPaused.result : false
  };
}
async function getUserVaultPosition(publicClient, vaultAddress, userAddress, assetDecimals = 6) {
  const shareDecimals = assetDecimals + 6;
  const c = (functionName, args) => ({
    address: vaultAddress,
    abi: VAULT_ABI,
    functionName,
    args
  });
  const init = await publicClient.multicall({
    contracts: [
      { address: vaultAddress, abi: VAULT_ABI, functionName: "nextRequestId" },
      { address: vaultAddress, abi: VAULT_ABI, functionName: "balanceOf", args: [userAddress] }
    ],
    allowFailure: true
  });
  const nextRequestId = Number(init[0].status === "success" ? init[0].result : 0n);
  const shareBalance = fromWei(
    init[1].status === "success" ? init[1].result : 0n,
    shareDecimals
  );
  if (nextRequestId === 0) {
    return { shareBalance, pendingShares: "0", claimableShares: "0", claimableAssets: "0", requestIds: [] };
  }
  const start = Math.max(1, nextRequestId - 99);
  const ids = Array.from({ length: nextRequestId - start + 1 }, (_, i) => BigInt(start + i));
  const contracts = [
    ...ids.map((id) => c("pendingRedeemRequest", [id, userAddress])),
    ...ids.map((id) => c("claimableRedeemRequest", [id, userAddress]))
  ];
  const results = await publicClient.multicall({ contracts, allowFailure: true });
  const pendingResults = results.slice(0, ids.length);
  const claimableResults = results.slice(ids.length);
  let totalPendingShares = 0n;
  let totalClaimableShares = 0n;
  const requestIds = [];
  const claimableRequestIds = [];
  ids.forEach((id, i) => {
    const pending = pendingResults[i].status === "success" ? pendingResults[i].result : 0n;
    const claimable = claimableResults[i].status === "success" ? claimableResults[i].result : 0n;
    if (pending > 0n || claimable > 0n) {
      requestIds.push(Number(id));
      totalPendingShares += pending;
      totalClaimableShares += claimable;
      if (claimable > 0n) claimableRequestIds.push(id);
    }
  });
  let claimableAssetsWei = 0n;
  if (claimableRequestIds.length > 0) {
    const sims = await Promise.all(
      claimableRequestIds.map(
        (id) => publicClient.simulateContract({
          account: userAddress,
          address: vaultAddress,
          abi: VAULT_ABI,
          functionName: "claimRedeem",
          args: [id, userAddress]
        }).catch((e) => {
          console.error(`[getUserVaultPosition] claimRedeem simulate failed for request ${id}:`, e);
          return null;
        })
      )
    );
    for (const sim of sims) {
      if (sim) claimableAssetsWei += sim.result;
    }
  }
  return {
    shareBalance,
    pendingShares: fromWei(totalPendingShares, shareDecimals),
    claimableShares: fromWei(totalClaimableShares, shareDecimals),
    claimableAssets: fromWei(claimableAssetsWei, assetDecimals),
    requestIds
  };
}
async function getDepositorsPositions(publicClient, vaultAddress, wallets, assetDecimals = 8, accountingDecimals = 6) {
  if (wallets.length === 0) return [];
  const shareDecimals = assetDecimals + 6;
  const balances = await publicClient.multicall({
    contracts: wallets.map(
      (w) => ({ address: vaultAddress, abi: VAULT_ABI, functionName: "balanceOf", args: [w] })
    ),
    allowFailure: true
  });
  const shareBalances = balances.map((r) => r.status === "success" ? r.result : 0n);
  const holders = wallets.map((wallet, i) => ({ wallet, i, bal: shareBalances[i] })).filter((h) => h.bal > 0n);
  const cbtcByIndex = /* @__PURE__ */ new Map();
  const usdByIndex = /* @__PURE__ */ new Map();
  if (holders.length > 0) {
    const n = holders.length;
    const results = await publicClient.multicall({
      contracts: [
        ...holders.map(
          (h) => ({ address: vaultAddress, abi: VAULT_ABI, functionName: "convertToAssets", args: [h.bal] })
        ),
        ...holders.map(
          (h) => ({ address: vaultAddress, abi: VAULT_ABI, functionName: "currentWalletExposure", args: [h.wallet] })
        )
      ],
      allowFailure: true
    });
    holders.forEach((h, k) => {
      const rc = results[k];
      cbtcByIndex.set(h.i, rc.status === "success" ? rc.result : 0n);
      const ru = results[k + n];
      usdByIndex.set(h.i, ru.status === "success" ? ru.result : 0n);
    });
  }
  return wallets.map((wallet, i) => {
    const sharesWei = shareBalances[i];
    const currentValueCbtcWei = cbtcByIndex.get(i) ?? 0n;
    const currentValueUsdWei = usdByIndex.get(i) ?? 0n;
    return {
      wallet,
      shares: fromWei(sharesWei, shareDecimals),
      currentValueCbtc: fromWei(currentValueCbtcWei, assetDecimals),
      currentValueUsd: fromWei(currentValueUsdWei, accountingDecimals),
      sharesWei,
      currentValueCbtcWei,
      currentValueUsdWei
    };
  });
}
async function getRedeemRequest(publicClient, vaultAddress, requestId, controller) {
  const results = await publicClient.multicall({
    contracts: [
      { address: vaultAddress, abi: VAULT_ABI, functionName: "redeemRequests", args: [BigInt(requestId)] },
      { address: vaultAddress, abi: VAULT_ABI, functionName: "pendingRedeemRequest", args: [BigInt(requestId), controller] },
      { address: vaultAddress, abi: VAULT_ABI, functionName: "claimableRedeemRequest", args: [BigInt(requestId), controller] }
    ],
    allowFailure: true
  });
  const reqData = results[0].status === "success" ? results[0].result : ["0x0000000000000000000000000000000000000000", 0n, false];
  const pending = results[1].status === "success" ? results[1].result : 0n;
  const claimable = results[2].status === "success" ? results[2].result : 0n;
  return {
    controller: reqData[0],
    shares: fromWei(reqData[1]),
    claimed: reqData[2],
    isPending: pending > 0n,
    isClaimable: claimable > 0n
  };
}
async function getConsentAcknowledgementsHash(publicClient, vaultAddress) {
  return publicClient.readContract({
    address: vaultAddress,
    abi: VAULT_ABI,
    functionName: "consentAcknowledgementsHash"
  });
}
async function hasCurrentConsent(publicClient, vaultAddress, wallet) {
  return publicClient.readContract({
    address: vaultAddress,
    abi: VAULT_ABI,
    functionName: "hasCurrentConsent",
    args: [wallet]
  });
}
async function getWalletCapStatus(publicClient, vaultAddress, wallet, decimals) {
  const results = await publicClient.multicall({
    contracts: [
      { address: vaultAddress, abi: VAULT_ABI, functionName: "walletCapConfigured", args: [wallet] },
      { address: vaultAddress, abi: VAULT_ABI, functionName: "effectiveWalletCap", args: [wallet] },
      { address: vaultAddress, abi: VAULT_ABI, functionName: "defaultWalletCap" },
      { address: vaultAddress, abi: VAULT_ABI, functionName: "currentWalletExposure", args: [wallet] },
      { address: vaultAddress, abi: VAULT_ABI, functionName: "maxDeposit", args: [wallet] }
    ],
    allowFailure: true
  });
  const configured = results[0].status === "success" ? results[0].result : false;
  const effectiveCapWei = results[1].status === "success" ? results[1].result : 0n;
  const defaultCapWei = results[2].status === "success" ? results[2].result : 0n;
  const exposureWei = results[3].status === "success" ? results[3].result : 0n;
  const maxDepositWei = results[4].status === "success" ? results[4].result : 0n;
  return {
    configured,
    effectiveCap: fmtCap(effectiveCapWei, decimals.accountingDecimals),
    defaultCap: fmtCap(defaultCapWei, decimals.accountingDecimals),
    exposure: fromWei(exposureWei, decimals.accountingDecimals),
    maxDeposit: fromWei(maxDepositWei, decimals.assetDecimals),
    effectiveCapWei,
    maxDepositWei
  };
}
async function getVaultWhitelist(publicClient, vaultAddress, accountingDecimals) {
  const [defaultRes, overridesRes] = await publicClient.multicall({
    contracts: [
      { address: vaultAddress, abi: VAULT_ABI, functionName: "defaultWalletCap" },
      { address: vaultAddress, abi: VAULT_ABI, functionName: "walletCapOverrides" }
    ],
    allowFailure: true
  });
  const defaultCapWei = defaultRes.status === "success" ? defaultRes.result : MAX_UINT256;
  const overrides = overridesRes.status === "success" ? overridesRes.result : [[], []];
  const [wallets, caps] = overrides;
  const entries = wallets.map((wallet, i) => ({
    wallet,
    cap: fmtCap(caps[i], accountingDecimals),
    capWei: caps[i]
  }));
  return {
    defaultCap: fmtCap(defaultCapWei, accountingDecimals),
    defaultCapWei,
    entries
  };
}
async function getVaultAccessControl(publicClient, vaultAddress, wallet) {
  const contracts = wallet ? [
    { address: vaultAddress, abi: VAULT_ABI, functionName: "owner" },
    { address: vaultAddress, abi: VAULT_ABI, functionName: "hasRole", args: [ADMIN_ROLE, wallet] },
    { address: vaultAddress, abi: VAULT_ABI, functionName: "hasRole", args: [DEFAULT_ADMIN_ROLE, wallet] }
  ] : [{ address: vaultAddress, abi: VAULT_ABI, functionName: "owner" }];
  const results = await publicClient.multicall({ contracts, allowFailure: true });
  if (results[0].status !== "success") {
    const err = results[0].error;
    throw new Error(
      `Vault owner() read failed for ${vaultAddress}: ${err instanceof Error ? err.message : String(err)}`
    );
  }
  const owner = results[0].result;
  let isAdmin = false;
  let hasDefaultAdmin = false;
  if (wallet) {
    if (results[1]?.status !== "success") {
      const err = results[1]?.error;
      throw new Error(
        `Vault hasRole(ADMIN_ROLE) read failed \u2014 likely a stale VAULT_ABI without AccessControl. Underlying: ${err instanceof Error ? err.message : String(err)}`
      );
    }
    if (results[2]?.status !== "success") {
      const err = results[2]?.error;
      throw new Error(
        `Vault hasRole(DEFAULT_ADMIN_ROLE) read failed. Underlying: ${err instanceof Error ? err.message : String(err)}`
      );
    }
    isAdmin = results[1].result;
    hasDefaultAdmin = results[2].result;
  }
  const isOwner = !!wallet && wallet.toLowerCase() === owner.toLowerCase();
  return {
    owner,
    isOwner,
    isAdmin,
    hasDefaultAdmin,
    isAuthorized: isOwner || isAdmin
  };
}
async function getVaultAdminGrants(publicClient, vaultAddress, wallets) {
  if (wallets.length === 0) return [];
  const results = await publicClient.multicall({
    contracts: wallets.map(
      (w) => ({
        address: vaultAddress,
        abi: VAULT_ABI,
        functionName: "hasRole",
        args: [ADMIN_ROLE, w]
      })
    ),
    allowFailure: true
  });
  const allFailed = results.every((r) => r.status !== "success");
  if (allFailed && results.length > 0) {
    const err = results[0].status === "failure" ? results[0].error : void 0;
    throw new Error(
      `Vault hasRole(ADMIN_ROLE) batch read failed for ALL wallets \u2014 likely a stale VAULT_ABI. Underlying: ${err instanceof Error ? err.message : String(err)}`
    );
  }
  return wallets.map((wallet, i) => ({
    wallet,
    hasAdmin: results[i].status === "success" ? results[i].result : false
  }));
}
async function getEpochInfo(publicClient, vaultAddress, epochId, assetDecimals = 6) {
  const id = BigInt(epochId);
  const results = await publicClient.multicall({
    contracts: [
      { address: vaultAddress, abi: VAULT_ABI, functionName: "epochSettlementSharePrice", args: [id] },
      { address: vaultAddress, abi: VAULT_ABI, functionName: "epochPendingShares", args: [id] },
      { address: vaultAddress, abi: VAULT_ABI, functionName: "epochClaimableAssets", args: [id] },
      { address: vaultAddress, abi: VAULT_ABI, functionName: "epochFinalized", args: [id] },
      { address: vaultAddress, abi: VAULT_ABI, functionName: "epochEndRequestId", args: [id] }
    ],
    allowFailure: true
  });
  return {
    epochId,
    settlementSharePrice: fromWei(results[0].status === "success" ? results[0].result : 0n, assetDecimals),
    pendingShares: fromWei(results[1].status === "success" ? results[1].result : 0n),
    claimableAssets: fromWei(results[2].status === "success" ? results[2].result : 0n, assetDecimals),
    finalized: results[3].status === "success" ? results[3].result : false,
    endRequestId: Number(results[4].status === "success" ? results[4].result : 0n)
  };
}
var VAULT_ABI2 = artifacts$1.ThemaLPVault.abi;
function buildDepositTx(vaultAddress, assetsWei, receiver) {
  return {
    address: vaultAddress,
    abi: VAULT_ABI2,
    functionName: "deposit",
    args: [assetsWei, receiver]
  };
}
function buildRequestRedeemTx(vaultAddress, sharesWei, controller, owner) {
  return {
    address: vaultAddress,
    abi: VAULT_ABI2,
    functionName: "requestRedeem",
    args: [sharesWei, controller, owner]
  };
}
function buildClaimRedeemTx(vaultAddress, requestId, receiver) {
  return {
    address: vaultAddress,
    abi: VAULT_ABI2,
    functionName: "claimRedeem",
    args: [requestId, receiver]
  };
}
function buildSettleEpochTx(vaultAddress) {
  return {
    address: vaultAddress,
    abi: VAULT_ABI2,
    functionName: "settleEpoch",
    args: []
  };
}
function buildPrepareFinalizeEpochTx(vaultAddress, epochId) {
  return {
    address: vaultAddress,
    abi: VAULT_ABI2,
    functionName: "prepareFinalizeEpoch",
    args: [epochId]
  };
}
function buildFinalizeEpochTx(vaultAddress, epochId) {
  return {
    address: vaultAddress,
    abi: VAULT_ABI2,
    functionName: "finalizeEpoch",
    args: [epochId]
  };
}
function buildSetDepositCapTx(vaultAddress, newCapWei) {
  return {
    address: vaultAddress,
    abi: VAULT_ABI2,
    functionName: "setDepositCap",
    args: [newCapWei]
  };
}
function buildSetDefaultWalletCapTx(vaultAddress, newCapWei) {
  return {
    address: vaultAddress,
    abi: VAULT_ABI2,
    functionName: "setDefaultWalletCap",
    args: [newCapWei]
  };
}
function buildSetWalletCapTx(vaultAddress, wallet, newCapWei) {
  return {
    address: vaultAddress,
    abi: VAULT_ABI2,
    functionName: "setWalletCap",
    args: [wallet, newCapWei]
  };
}
function buildClearWalletCapTx(vaultAddress, wallet) {
  return {
    address: vaultAddress,
    abi: VAULT_ABI2,
    functionName: "clearWalletCap",
    args: [wallet]
  };
}
function buildSetWalletCapsTx(vaultAddress, wallets, newCapsWei) {
  return {
    address: vaultAddress,
    abi: VAULT_ABI2,
    functionName: "setWalletCaps",
    args: [wallets, newCapsWei]
  };
}
function buildGrantRoleTx(vaultAddress, role, wallet) {
  return {
    address: vaultAddress,
    abi: VAULT_ABI2,
    functionName: "grantRole",
    args: [role, wallet]
  };
}
function buildRevokeRoleTx(vaultAddress, role, wallet) {
  return {
    address: vaultAddress,
    abi: VAULT_ABI2,
    functionName: "revokeRole",
    args: [role, wallet]
  };
}
function buildSetSettlerTx(vaultAddress, newSettler) {
  return {
    address: vaultAddress,
    abi: VAULT_ABI2,
    functionName: "setSettler",
    args: [newSettler]
  };
}
function buildSweepExcessTx(vaultAddress) {
  return {
    address: vaultAddress,
    abi: VAULT_ABI2,
    functionName: "sweepExcess",
    args: []
  };
}
function buildRecordConsentTx(vaultAddress, expectedAcknowledgementsHash) {
  return {
    address: vaultAddress,
    abi: VAULT_ABI2,
    functionName: "recordConsent",
    args: [expectedAcknowledgementsHash]
  };
}
function buildSetConsentAcknowledgementsHashTx(vaultAddress, newHash) {
  return {
    address: vaultAddress,
    abi: VAULT_ABI2,
    functionName: "setConsentAcknowledgementsHash",
    args: [newHash]
  };
}
function buildPauseTx(vaultAddress) {
  return {
    address: vaultAddress,
    abi: VAULT_ABI2,
    functionName: "pause",
    args: []
  };
}
function buildUnpauseTx(vaultAddress) {
  return {
    address: vaultAddress,
    abi: VAULT_ABI2,
    functionName: "unpause",
    args: []
  };
}
function parseAndBuildDeposit(vaultAddress, assets, receiver, assetDecimals = 6) {
  return buildDepositTx(vaultAddress, parseUnits(assets, assetDecimals), receiver);
}
function parseAndBuildRequestRedeem(vaultAddress, shares, controller, owner) {
  return buildRequestRedeemTx(vaultAddress, parseUnits(shares, 18), controller, owner);
}
var SAFE_ABI = artifacts$1.Safe.abi;
var SAFE_MODULES_ABI = [
  {
    inputs: [
      { name: "start", type: "address" },
      { name: "pageSize", type: "uint256" }
    ],
    name: "getModulesPaginated",
    outputs: [
      { name: "array", type: "address[]" },
      { name: "next", type: "address" }
    ],
    stateMutability: "view",
    type: "function"
  }
];
var GUARD_STORAGE_SLOT = "0x4a204f620c8c5ccdca3fd54d003badd85ba500436a431f0cbda4f558c93c34c8";
var ZERO_ADDRESS = "0x0000000000000000000000000000000000000000";
var SENTINEL = "0x0000000000000000000000000000000000000001";
async function getSafeModulesAndGuard(chainId, safeAddress, rpcUrl) {
  const publicClient = createSafePublicClient(chainId, rpcUrl);
  const [modulesResult, guardResult] = await Promise.allSettled([
    publicClient.readContract({
      address: safeAddress,
      abi: SAFE_MODULES_ABI,
      functionName: "getModulesPaginated",
      args: [SENTINEL, 100n]
    }),
    publicClient.getStorageAt({ address: safeAddress, slot: GUARD_STORAGE_SLOT })
  ]);
  const modules = modulesResult.status === "fulfilled" ? modulesResult.value[0] : [];
  let guardAddress = null;
  if (guardResult.status === "fulfilled" && guardResult.value) {
    const raw = guardResult.value;
    if (raw !== "0x" && raw !== `0x${"0".repeat(64)}`) {
      const addr = `0x${raw.slice(-40)}`;
      if (addr.toLowerCase() !== ZERO_ADDRESS) {
        guardAddress = addr;
      }
    }
  }
  return { modules, guardAddress };
}
var SAFE_SERVICE_URLS = {
  1: "https://safe-transaction-mainnet.safe.global",
  10: "https://safe-transaction-optimism.safe.global",
  56: "https://safe-transaction-bsc.safe.global",
  100: "https://safe-transaction-gnosis-chain.safe.global",
  137: "https://safe-transaction-polygon.safe.global",
  8453: "https://safe-transaction-base.safe.global",
  42161: "https://safe-transaction-arbitrum.safe.global",
  43114: "https://safe-transaction-avalanche.safe.global",
  11155111: "https://safe-transaction-sepolia.safe.global",
  84532: "https://safe-transaction-base-sepolia.safe.global"
};
function getSafeServiceUrl(chainId) {
  const url = SAFE_SERVICE_URLS[chainId];
  if (!url) throw new Error(`No Safe Transaction Service configured for chainId ${chainId}`);
  return url;
}
function isSafeServiceSupported(chainId) {
  return chainId in SAFE_SERVICE_URLS;
}
var SAFE_EXEC_ABI = [
  {
    inputs: [
      { name: "to", type: "address" },
      { name: "value", type: "uint256" },
      { name: "data", type: "bytes" },
      { name: "operation", type: "uint8" },
      { name: "safeTxGas", type: "uint256" },
      { name: "baseGas", type: "uint256" },
      { name: "gasPrice", type: "uint256" },
      { name: "gasToken", type: "address" },
      { name: "refundReceiver", type: "address" },
      { name: "signatures", type: "bytes" }
    ],
    name: "execTransaction",
    outputs: [{ name: "success", type: "bool" }],
    stateMutability: "payable",
    type: "function"
  }
];
var ERC20_READ_ABI = [
  {
    inputs: [{ name: "account", type: "address" }],
    name: "balanceOf",
    outputs: [{ name: "", type: "uint256" }],
    stateMutability: "view",
    type: "function"
  },
  {
    inputs: [],
    name: "decimals",
    outputs: [{ name: "", type: "uint8" }],
    stateMutability: "view",
    type: "function"
  },
  {
    inputs: [],
    name: "symbol",
    outputs: [{ name: "", type: "string" }],
    stateMutability: "view",
    type: "function"
  },
  {
    inputs: [],
    name: "name",
    outputs: [{ name: "", type: "string" }],
    stateMutability: "view",
    type: "function"
  }
];
function buildSafeTxTypedData(chainId, safeAddress, tx) {
  return {
    domain: {
      chainId,
      verifyingContract: safeAddress
    },
    types: {
      SafeTx: [
        { name: "to", type: "address" },
        { name: "value", type: "uint256" },
        { name: "data", type: "bytes" },
        { name: "operation", type: "uint8" },
        { name: "safeTxGas", type: "uint256" },
        { name: "baseGas", type: "uint256" },
        { name: "gasPrice", type: "uint256" },
        { name: "gasToken", type: "address" },
        { name: "refundReceiver", type: "address" },
        { name: "nonce", type: "uint256" }
      ]
    },
    primaryType: "SafeTx",
    message: tx
  };
}
function createSafePublicClient(chainId, rpcUrl) {
  const { chain } = getChainConfig(String(chainId));
  const url = rpcUrl ?? getDefaultRpcUrl(String(chainId));
  return createPublicClient({ chain, transport: http(url) });
}
function fromWei2(value, decimals = 18) {
  const divisor = 10n ** BigInt(decimals);
  const whole = value / divisor;
  const frac = value % divisor;
  if (frac === 0n) return whole.toString();
  const fracStr = frac.toString().padStart(decimals, "0").replace(/0+$/, "");
  return `${whole}.${fracStr}`;
}
async function getSafeOnChainState(chainId, safeAddress, rpcUrl) {
  const publicClient = createSafePublicClient(chainId, rpcUrl);
  const [owners, threshold, nonce] = await Promise.all([
    publicClient.readContract({ address: safeAddress, abi: SAFE_ABI, functionName: "getOwners" }),
    publicClient.readContract({ address: safeAddress, abi: SAFE_ABI, functionName: "getThreshold" }),
    publicClient.readContract({ address: safeAddress, abi: SAFE_ABI, functionName: "nonce" })
  ]);
  return {
    address: safeAddress,
    owners,
    threshold: Number(threshold),
    nonce: Number(nonce)
  };
}
async function getSafeNativeBalance(chainId, safeAddress, rpcUrl) {
  const publicClient = createSafePublicClient(chainId, rpcUrl);
  const balance = await publicClient.getBalance({ address: safeAddress });
  return fromWei2(balance, 18);
}
async function getSafeTokenBalances(chainId, safeAddress, tokenAddresses, rpcUrl) {
  const publicClient = createSafePublicClient(chainId, rpcUrl);
  const results = await Promise.allSettled(
    tokenAddresses.map(async (tokenAddress) => {
      const [balance, decimals, symbol, name] = await Promise.all([
        publicClient.readContract({ address: tokenAddress, abi: ERC20_READ_ABI, functionName: "balanceOf", args: [safeAddress] }),
        publicClient.readContract({ address: tokenAddress, abi: ERC20_READ_ABI, functionName: "decimals" }),
        publicClient.readContract({ address: tokenAddress, abi: ERC20_READ_ABI, functionName: "symbol" }),
        publicClient.readContract({ address: tokenAddress, abi: ERC20_READ_ABI, functionName: "name" })
      ]);
      const dec = Number(decimals);
      const bal = balance;
      return {
        tokenAddress,
        symbol,
        name,
        decimals: dec,
        balance: fromWei2(bal, dec),
        balanceRaw: bal.toString()
      };
    })
  );
  return results.filter((r) => r.status === "fulfilled").map((r) => r.value);
}
async function getSafePortfolio(chainId, safeAddress, tokenAddresses, rpcUrl) {
  const [nativeBalance, tokenBalances] = await Promise.all([
    getSafeNativeBalance(chainId, safeAddress, rpcUrl),
    getSafeTokenBalances(chainId, safeAddress, tokenAddresses, rpcUrl)
  ]);
  return { nativeBalance, tokenBalances };
}
async function safeServiceError(res) {
  try {
    const text = await res.text();
    try {
      const json = JSON.parse(text);
      const msg = json.message ?? json.detail ?? JSON.stringify(json);
      return new Error(`Safe service ${res.status}: ${msg}`);
    } catch {
      if (text.trim().startsWith("<")) {
        return new Error(`Safe service unavailable (${res.status})`);
      }
      return new Error(`Safe service ${res.status}: ${text.slice(0, 200)}`);
    }
  } catch {
    return new Error(`Safe service error (${res.status})`);
  }
}
async function getSafeServiceInfo(chainId, safeAddress) {
  const baseUrl = getSafeServiceUrl(chainId);
  const res = await fetch(`${baseUrl}/api/v1/safes/${safeAddress}/`);
  if (!res.ok) throw await safeServiceError(res);
  return res.json();
}
async function getOwnerSafes(chainId, ownerAddress) {
  const baseUrl = getSafeServiceUrl(chainId);
  const res = await fetch(`${baseUrl}/api/v1/owners/${ownerAddress}/safes/`);
  if (!res.ok) throw await safeServiceError(res);
  const data = await res.json();
  return data.safes;
}
async function getSafePendingTransactions(chainId, safeAddress, limit = 20) {
  const baseUrl = getSafeServiceUrl(chainId);
  const url = `${baseUrl}/api/v1/safes/${safeAddress}/multisig-transactions/?executed=false&limit=${limit}&ordering=-nonce`;
  const res = await fetch(url);
  if (!res.ok) throw await safeServiceError(res);
  return res.json();
}
async function getSafeTransactionHistory(chainId, safeAddress, limit = 20) {
  const baseUrl = getSafeServiceUrl(chainId);
  const url = `${baseUrl}/api/v1/safes/${safeAddress}/multisig-transactions/?executed=true&limit=${limit}&ordering=-executionDate`;
  const res = await fetch(url);
  if (!res.ok) throw await safeServiceError(res);
  return res.json();
}
async function getSafeTransaction(chainId, safeTxHash) {
  const baseUrl = getSafeServiceUrl(chainId);
  const res = await fetch(`${baseUrl}/api/v1/multisig-transactions/${safeTxHash}/`);
  if (!res.ok) throw await safeServiceError(res);
  return res.json();
}
async function proposeSafeTransaction(chainId, safeAddress, params) {
  const baseUrl = getSafeServiceUrl(chainId);
  const res = await fetch(`${baseUrl}/api/v1/safes/${safeAddress}/multisig-transactions/`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(params)
  });
  if (!res.ok) throw await safeServiceError(res);
}
async function confirmSafeTransaction(chainId, safeTxHash, signature) {
  const baseUrl = getSafeServiceUrl(chainId);
  const res = await fetch(`${baseUrl}/api/v1/multisig-transactions/${safeTxHash}/confirmations/`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ signature })
  });
  if (!res.ok) throw await safeServiceError(res);
}

// src/ui/types/Identifiers.ts
function createChainId(id) {
  return id.toString();
}
function toAddress(value) {
  if (!value.startsWith("0x")) {
    throw new Error("Address must start with 0x");
  }
  if (!/^0x[0-9a-fA-F]{40}$/.test(value)) {
    throw new Error("Invalid address format");
  }
  return value;
}
function validateEvmAddress(value) {
  return /^0x[0-9a-fA-F]{40}$/.test(value);
}
function validateSolanaAddress(_) {
  return true;
}
function validateAddress(value, chainType) {
  switch (chainType) {
    case "evm" /* EVM */:
    case "anvil" /* ANVIL */:
      return validateEvmAddress(value);
    case "solana" /* SOLANA */:
      return validateSolanaAddress();
    default:
      return false;
  }
}

// src/ui/themaWallet/themaWalletConfig.ts
var THEMA_SAFE_CONFIGS = [
  {
    id: "tbtc01-base",
    name: "tBTC Liquidity Safe",
    safeAddress: "0x774F1036b9573228f030b4DfBC70ca3F39ee84fc",
    walletModuleAddress: null,
    chainId: "8453",
    chainName: "Base",
    rpcUrl: "https://base-mainnet.g.alchemy.com/v2/QxfVVboGXQ_rg3580s1TIJCNZTkQzoRh",
    nativeSymbol: "ETH",
    nativeOracleAddress: "0x71041dddad3595F9CEd3DcCFBe3D1F4b0a16Bb70",
    nativeOracleDecimals: 8,
    tokens: [
      {
        address: "0x833589fCD6eDb6E08f4c7C32D4f71b54bdA02913",
        symbol: "USDC",
        decimals: 6,
        oracleAddress: "0x7e860098F58bBFC8648a4311b374B1D669a2bc6B",
        oracleDecimals: 8
      },
      {
        address: "0xcbB7C0000aB88B473b1f5aFd9ef808440eed33Bf",
        symbol: "cbBTC",
        decimals: 8,
        oracleAddress: "0x07DA0E54543a844a80ABE69c8A12F22B3aA59f9D",
        oracleDecimals: 8
      },
      {
        address: "0x4200000000000000000000000000000000000006",
        symbol: "WETH",
        decimals: 18,
        oracleAddress: "0x71041dddad3595F9CEd3DcCFBe3D1F4b0a16Bb70",
        oracleDecimals: 8
      },
      {
        address: "0x395e2cd3fba0da8818613e4e661ca83eac3f888c",
        symbol: "TBTC01",
        decimals: 18,
        oracleAddress: "0x3680677A620A39420D3b04A7667a2756e4E45eC6",
        oracleDecimals: 8
      }
    ],
    stablecoin: "0x833589fCD6eDb6E08f4c7C32D4f71b54bdA02913",
    vaultAddress: "0xDd974e5B9a65317623d7008Ed1797d4eca9e551e",
    guardModuleAddress: null
  }
];
function getThemaSafe(id) {
  return THEMA_SAFE_CONFIGS.find((s) => s.id === id);
}
function getThemaSafeByAddress(safeAddress) {
  return THEMA_SAFE_CONFIGS.find(
    (s) => s.safeAddress.toLowerCase() === safeAddress.toLowerCase()
  );
}
var ERC20_BALANCE_ABI = [
  {
    inputs: [{ name: "account", type: "address" }],
    name: "balanceOf",
    outputs: [{ name: "", type: "uint256" }],
    stateMutability: "view",
    type: "function"
  }
];
var CHAINLINK_AGGREGATOR_ABI = [
  {
    inputs: [],
    name: "latestRoundData",
    outputs: [
      { name: "roundId", type: "uint80" },
      { name: "answer", type: "int256" },
      { name: "startedAt", type: "uint256" },
      { name: "updatedAt", type: "uint256" },
      { name: "answeredInRound", type: "uint80" }
    ],
    stateMutability: "view",
    type: "function"
  }
];
var THEMA_WALLET_ABI = [
  // ── View ─────────────────────────────────────────────────────────────────
  { inputs: [], name: "safe", outputs: [{ name: "", type: "address" }], stateMutability: "view", type: "function" },
  { inputs: [], name: "paused", outputs: [{ name: "", type: "bool" }], stateMutability: "view", type: "function" },
  { inputs: [], name: "quoteSigner", outputs: [{ name: "", type: "address" }], stateMutability: "view", type: "function" },
  { inputs: [], name: "vaultAccounting", outputs: [{ name: "", type: "address" }], stateMutability: "view", type: "function" },
  { inputs: [], name: "isModuleEnabled", outputs: [{ name: "enabled", type: "bool" }], stateMutability: "view", type: "function" },
  { inputs: [], name: "activePositionCount", outputs: [{ name: "", type: "uint256" }], stateMutability: "view", type: "function" },
  { inputs: [], name: "getActivePositionIds", outputs: [{ name: "", type: "uint256[]" }], stateMutability: "view", type: "function" },
  { inputs: [], name: "totalExternalCapital", outputs: [{ name: "", type: "uint256" }], stateMutability: "view", type: "function" },
  { inputs: [], name: "operatorCapitalBase", outputs: [{ name: "", type: "uint256" }], stateMutability: "view", type: "function" },
  { inputs: [], name: "oracle", outputs: [{ name: "", type: "address" }], stateMutability: "view", type: "function" },
  { inputs: [], name: "tvlPositionManager", outputs: [{ name: "", type: "address" }], stateMutability: "view", type: "function" },
  { inputs: [], name: "v3Factory", outputs: [{ name: "", type: "address" }], stateMutability: "view", type: "function" },
  { inputs: [], name: "tvlTwapWindow", outputs: [{ name: "", type: "uint32" }], stateMutability: "view", type: "function" },
  {
    inputs: [{ name: "vault", type: "address" }, { name: "stablecoin", type: "address" }],
    name: "computeSafeTVL",
    outputs: [{ name: "spotTVL", type: "uint256" }, { name: "twapTVL", type: "uint256" }],
    stateMutability: "view",
    type: "function"
  },
  {
    inputs: [{ name: "vault", type: "address" }],
    name: "vaultCapitalDeposited",
    outputs: [{ name: "", type: "uint256" }],
    stateMutability: "view",
    type: "function"
  },
  {
    inputs: [{ name: "vault", type: "address" }],
    name: "vaultTotalOut",
    outputs: [{ name: "", type: "uint256" }],
    stateMutability: "view",
    type: "function"
  },
  {
    inputs: [{ name: "vault", type: "address" }],
    name: "allowedVaults",
    outputs: [{ name: "", type: "bool" }],
    stateMutability: "view",
    type: "function"
  },
  {
    inputs: [{ name: "agent", type: "address" }],
    name: "allowedAgents",
    outputs: [{ name: "", type: "bool" }],
    stateMutability: "view",
    type: "function"
  },
  {
    inputs: [{ name: "", type: "address" }],
    name: "allowedAssets",
    outputs: [{ name: "", type: "bool" }],
    stateMutability: "view",
    type: "function"
  },
  {
    inputs: [{ name: "", type: "address" }],
    name: "allowedProtocols",
    outputs: [{ name: "", type: "bool" }],
    stateMutability: "view",
    type: "function"
  },
  // ── Write (onlyOwner) ─────────────────────────────────────────────────────
  { inputs: [{ name: "agent", type: "address" }], name: "addAgent", outputs: [], stateMutability: "nonpayable", type: "function" },
  { inputs: [{ name: "agent", type: "address" }], name: "removeAgent", outputs: [], stateMutability: "nonpayable", type: "function" },
  { inputs: [{ name: "protocol", type: "address" }], name: "addProtocol", outputs: [], stateMutability: "nonpayable", type: "function" },
  { inputs: [{ name: "protocols", type: "address[]" }], name: "addProtocolsInBatch", outputs: [], stateMutability: "nonpayable", type: "function" },
  { inputs: [{ name: "protocol", type: "address" }], name: "removeProtocol", outputs: [], stateMutability: "nonpayable", type: "function" },
  { inputs: [{ name: "asset", type: "address" }], name: "addAsset", outputs: [], stateMutability: "nonpayable", type: "function" },
  { inputs: [{ name: "assets", type: "address[]" }], name: "addAssetsInBatch", outputs: [], stateMutability: "nonpayable", type: "function" },
  { inputs: [{ name: "asset", type: "address" }], name: "removeAsset", outputs: [], stateMutability: "nonpayable", type: "function" },
  {
    inputs: [{ name: "protocol", type: "address" }, { name: "selector", type: "bytes4" }],
    name: "addSelector",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function"
  },
  { inputs: [{ name: "signer", type: "address" }], name: "setQuoteSigner", outputs: [], stateMutability: "nonpayable", type: "function" },
  {
    inputs: [{ name: "protocol", type: "address" }, { name: "maxSlippageBps", type: "uint16" }],
    name: "setMaxSlippagePolicy",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function"
  },
  { inputs: [], name: "pause", outputs: [], stateMutability: "nonpayable", type: "function" },
  { inputs: [], name: "unpause", outputs: [], stateMutability: "nonpayable", type: "function" },
  { inputs: [{ name: "_vaultAccounting", type: "address" }], name: "setVaultAccounting", outputs: [], stateMutability: "nonpayable", type: "function" },
  { inputs: [{ name: "vault", type: "address" }], name: "registerVault", outputs: [], stateMutability: "nonpayable", type: "function" },
  { inputs: [{ name: "vault", type: "address" }], name: "removeVault", outputs: [], stateMutability: "nonpayable", type: "function" },
  { inputs: [{ name: "_oracle", type: "address" }], name: "setOracle", outputs: [], stateMutability: "nonpayable", type: "function" },
  { inputs: [{ name: "_pm", type: "address" }], name: "setTVLPositionManager", outputs: [], stateMutability: "nonpayable", type: "function" },
  { inputs: [{ name: "_v3Factory", type: "address" }], name: "setV3Factory", outputs: [], stateMutability: "nonpayable", type: "function" },
  { inputs: [{ name: "_twapWindow", type: "uint32" }], name: "setTvlTwapWindow", outputs: [], stateMutability: "nonpayable", type: "function" },
  { inputs: [{ name: "newBase", type: "uint256" }], name: "setOperatorCapitalBase", outputs: [], stateMutability: "nonpayable", type: "function" }
];
function fromWei3(value, decimals = 18) {
  if (value === 0n) return "0";
  const divisor = 10n ** BigInt(decimals);
  const whole = value / divisor;
  const frac = value % divisor;
  if (frac === 0n) return whole.toString();
  const fracStr = frac.toString().padStart(decimals, "0").replace(/0+$/, "");
  return `${whole}.${fracStr}`;
}
function mulStrings(a, b) {
  const result = parseFloat(a) * parseFloat(b);
  if (isNaN(result)) return "0";
  return result.toFixed(2);
}
function sumUsd(values) {
  return values.reduce((acc, v) => acc + parseFloat(v || "0"), 0).toFixed(2);
}
var THEMA_GUARD_ABI = [
  // ── View ──────────────────────────────────────────────────────────────────
  { inputs: [], name: "paused", outputs: [{ name: "", type: "bool" }], stateMutability: "view", type: "function" },
  { inputs: [], name: "safe", outputs: [{ name: "", type: "address" }], stateMutability: "view", type: "function" },
  { inputs: [], name: "themaWallet", outputs: [{ name: "", type: "address" }], stateMutability: "view", type: "function" },
  {
    inputs: [{ name: "", type: "address" }],
    name: "allowedTargets",
    outputs: [{ name: "", type: "bool" }],
    stateMutability: "view",
    type: "function"
  },
  {
    inputs: [{ name: "", type: "address" }, { name: "", type: "bytes4" }],
    name: "allowedSelectors",
    outputs: [{ name: "", type: "bool" }],
    stateMutability: "view",
    type: "function"
  },
  {
    inputs: [{ name: "", type: "bytes4" }],
    name: "allowedSafeSelfSelectors",
    outputs: [{ name: "", type: "bool" }],
    stateMutability: "view",
    type: "function"
  },
  {
    inputs: [{ name: "target", type: "address" }, { name: "selector", type: "bytes4" }],
    name: "isAllowed",
    outputs: [{ name: "allowed", type: "bool" }],
    stateMutability: "view",
    type: "function"
  },
  {
    inputs: [{ name: "target", type: "address" }],
    name: "isTargetAllowed",
    outputs: [{ name: "allowed", type: "bool" }],
    stateMutability: "view",
    type: "function"
  },
  // ── Write (onlyOwner) ─────────────────────────────────────────────────────
  { inputs: [], name: "pause", outputs: [], stateMutability: "nonpayable", type: "function" },
  { inputs: [], name: "unpause", outputs: [], stateMutability: "nonpayable", type: "function" },
  { inputs: [{ name: "target", type: "address" }], name: "addTarget", outputs: [], stateMutability: "nonpayable", type: "function" },
  { inputs: [{ name: "target", type: "address" }], name: "removeTarget", outputs: [], stateMutability: "nonpayable", type: "function" },
  { inputs: [{ name: "targets", type: "address[]" }], name: "addTargetsInBatch", outputs: [], stateMutability: "nonpayable", type: "function" },
  { inputs: [{ name: "targets", type: "address[]" }], name: "removeTargetsInBatch", outputs: [], stateMutability: "nonpayable", type: "function" },
  {
    inputs: [{ name: "target", type: "address" }, { name: "selector", type: "bytes4" }],
    name: "addSelector",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function"
  },
  {
    inputs: [{ name: "target", type: "address" }, { name: "selector", type: "bytes4" }],
    name: "removeSelector",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function"
  },
  {
    inputs: [{ name: "target", type: "address" }, { name: "selectors", type: "bytes4[]" }],
    name: "addSelectorsInBatch",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function"
  },
  {
    inputs: [{ name: "target", type: "address" }, { name: "selector", type: "bytes4" }, { name: "constraints", type: "uint8" }],
    name: "setSelectorConstraint",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function"
  },
  { inputs: [{ name: "selector", type: "bytes4" }], name: "addSafeSelfSelector", outputs: [], stateMutability: "nonpayable", type: "function" },
  { inputs: [{ name: "selector", type: "bytes4" }], name: "removeSafeSelfSelector", outputs: [], stateMutability: "nonpayable", type: "function" }
];
async function getThemaGuardState(guardAddress, publicClient) {
  const zero = "0x0000000000000000000000000000000000000000";
  const results = await publicClient.multicall({
    contracts: [
      { address: guardAddress, abi: THEMA_GUARD_ABI, functionName: "paused" },
      { address: guardAddress, abi: THEMA_GUARD_ABI, functionName: "safe" },
      { address: guardAddress, abi: THEMA_GUARD_ABI, functionName: "themaWallet" }
    ],
    allowFailure: true
  });
  return {
    paused: results[0].status === "success" ? results[0].result : false,
    safe: results[1].status === "success" ? results[1].result : zero,
    themaWallet: results[2].status === "success" ? results[2].result : zero
  };
}
async function safeGetLogs(fn) {
  try {
    return await fn();
  } catch {
    return [];
  }
}
async function getThemaWalletAllowlists(walletModuleAddress, publicClient) {
  const base2 = { address: walletModuleAddress, fromBlock: 0n };
  const [
    agentAdded,
    agentRemoved,
    protocolAdded,
    protocolRemoved,
    assetAdded,
    assetRemoved,
    selectorAdded,
    selectorRemoved,
    slippageLogs
  ] = await Promise.all([
    safeGetLogs(() => publicClient.getLogs({ ...base2, event: { type: "event", name: "AgentAdded", inputs: [{ name: "agent", type: "address", indexed: true }] } })),
    safeGetLogs(() => publicClient.getLogs({ ...base2, event: { type: "event", name: "AgentRemoved", inputs: [{ name: "agent", type: "address", indexed: true }] } })),
    safeGetLogs(() => publicClient.getLogs({ ...base2, event: { type: "event", name: "ProtocolAdded", inputs: [{ name: "protocol", type: "address", indexed: true }] } })),
    safeGetLogs(() => publicClient.getLogs({ ...base2, event: { type: "event", name: "ProtocolRemoved", inputs: [{ name: "protocol", type: "address", indexed: true }] } })),
    safeGetLogs(() => publicClient.getLogs({ ...base2, event: { type: "event", name: "AssetAdded", inputs: [{ name: "asset", type: "address", indexed: true }] } })),
    safeGetLogs(() => publicClient.getLogs({ ...base2, event: { type: "event", name: "AssetRemoved", inputs: [{ name: "asset", type: "address", indexed: true }] } })),
    safeGetLogs(() => publicClient.getLogs({ ...base2, event: { type: "event", name: "SelectorAdded", inputs: [{ name: "protocol", type: "address", indexed: true }, { name: "selector", type: "bytes4", indexed: true }] } })),
    safeGetLogs(() => publicClient.getLogs({ ...base2, event: { type: "event", name: "SelectorRemoved", inputs: [{ name: "protocol", type: "address", indexed: true }, { name: "selector", type: "bytes4", indexed: true }] } })),
    safeGetLogs(() => publicClient.getLogs({ ...base2, event: { type: "event", name: "MaxSlippagePolicyUpdated", inputs: [{ name: "protocol", type: "address", indexed: true }, { name: "maxSlippageBps", type: "uint16", indexed: false }] } }))
  ]);
  const agentSet = new Set(agentAdded.map((l) => l.args.agent.toLowerCase()));
  for (const l of agentRemoved) agentSet.delete(l.args.agent.toLowerCase());
  const protocolSet = new Set(protocolAdded.map((l) => l.args.protocol.toLowerCase()));
  for (const l of protocolRemoved) protocolSet.delete(l.args.protocol.toLowerCase());
  const assetSet = new Set(assetAdded.map((l) => l.args.asset.toLowerCase()));
  for (const l of assetRemoved) assetSet.delete(l.args.asset.toLowerCase());
  const selectorMap = {};
  for (const l of selectorAdded) {
    const proto = l.args.protocol.toLowerCase();
    const sel = l.args.selector;
    if (!selectorMap[proto]) selectorMap[proto] = /* @__PURE__ */ new Set();
    selectorMap[proto].add(sel);
  }
  for (const l of selectorRemoved) {
    const proto = l.args.protocol.toLowerCase();
    const sel = l.args.selector;
    selectorMap[proto]?.delete(sel);
  }
  const slippageMap = {};
  for (const l of slippageLogs) {
    const proto = l.args.protocol.toLowerCase();
    slippageMap[proto] = { bps: Number(l.args.maxSlippageBps), hasPolicy: true };
  }
  const selectorsByProtocol = {};
  for (const [proto, selSet] of Object.entries(selectorMap)) {
    selectorsByProtocol[proto] = [...selSet];
  }
  const slippageByProtocol = {};
  for (const [proto, policy] of Object.entries(slippageMap)) {
    slippageByProtocol[proto] = policy;
  }
  return {
    agents: [...agentSet],
    protocols: [...protocolSet],
    assets: [...assetSet],
    selectorsByProtocol,
    slippageByProtocol
  };
}
var NONFUNGIBLE_POSITION_MANAGER_ABI = [
  {
    inputs: [{ name: "tokenId", type: "uint256" }],
    name: "positions",
    outputs: [
      { name: "nonce", type: "uint96" },
      { name: "operator", type: "address" },
      { name: "token0", type: "address" },
      { name: "token1", type: "address" },
      { name: "fee", type: "uint24" },
      { name: "tickLower", type: "int24" },
      { name: "tickUpper", type: "int24" },
      { name: "liquidity", type: "uint128" },
      { name: "feeGrowthInside0LastX128", type: "uint256" },
      { name: "feeGrowthInside1LastX128", type: "uint256" },
      { name: "tokensOwed0", type: "uint128" },
      { name: "tokensOwed1", type: "uint128" }
    ],
    stateMutability: "view",
    type: "function"
  }
];
var UNISWAP_V3_FACTORY_ABI = [
  {
    inputs: [
      { name: "tokenA", type: "address" },
      { name: "tokenB", type: "address" },
      { name: "fee", type: "uint24" }
    ],
    name: "getPool",
    outputs: [{ name: "pool", type: "address" }],
    stateMutability: "view",
    type: "function"
  }
];
var UNISWAP_V3_POOL_ABI = [
  {
    inputs: [],
    name: "slot0",
    outputs: [
      { name: "sqrtPriceX96", type: "uint160" },
      { name: "tick", type: "int24" },
      { name: "observationIndex", type: "uint16" },
      { name: "observationCardinality", type: "uint16" },
      { name: "observationCardinalityNext", type: "uint16" },
      { name: "feeProtocol", type: "uint8" },
      { name: "unlocked", type: "bool" }
    ],
    stateMutability: "view",
    type: "function"
  }
];
var ERC20_SYMBOL_DECIMALS_ABI = [
  { inputs: [], name: "symbol", outputs: [{ name: "", type: "string" }], stateMutability: "view", type: "function" },
  { inputs: [], name: "decimals", outputs: [{ name: "", type: "uint8" }], stateMutability: "view", type: "function" }
];
async function getActivePositionDetails(walletModuleAddress, publicClient) {
  const zero = "0x0000000000000000000000000000000000000000";
  const moduleData = await publicClient.multicall({
    contracts: [
      { address: walletModuleAddress, abi: THEMA_WALLET_ABI, functionName: "tvlPositionManager" },
      { address: walletModuleAddress, abi: THEMA_WALLET_ABI, functionName: "v3Factory" },
      { address: walletModuleAddress, abi: THEMA_WALLET_ABI, functionName: "getActivePositionIds" }
    ],
    allowFailure: true
  });
  const pmAddress = moduleData[0].status === "success" ? moduleData[0].result : zero;
  const factoryAddress = moduleData[1].status === "success" ? moduleData[1].result : zero;
  const rawIds = moduleData[2].status === "success" ? moduleData[2].result : [];
  if (!rawIds.length || pmAddress === zero) return [];
  const positionResults = await publicClient.multicall({
    contracts: rawIds.map((id) => ({
      address: pmAddress,
      abi: NONFUNGIBLE_POSITION_MANAGER_ABI,
      functionName: "positions",
      args: [id]
    })),
    allowFailure: true
  });
  const rawPositions = positionResults.map((r, i) => {
    if (r.status !== "success") return null;
    const t = r.result;
    return {
      tokenId: rawIds[i].toString(),
      token0: t[2],
      token1: t[3],
      fee: Number(t[4]),
      tickLower: Number(t[5]),
      tickUpper: Number(t[6]),
      liquidity: t[7].toString(),
      tokensOwed0Raw: t[10],
      tokensOwed1Raw: t[11]
    };
  }).filter((p) => p !== null);
  if (!rawPositions.length) return [];
  const uniqueTokens = [
    ...new Set(rawPositions.flatMap((p) => [p.token0.toLowerCase(), p.token1.toLowerCase()]))
  ];
  const uniquePairKeys = [
    ...new Map(
      rawPositions.map((p) => {
        const key = `${p.token0.toLowerCase()}-${p.token1.toLowerCase()}-${p.fee}`;
        return [key, { token0: p.token0, token1: p.token1, fee: p.fee }];
      })
    ).values()
  ];
  const [tokenMetaResults, poolAddrResults] = await Promise.all([
    publicClient.multicall({
      contracts: uniqueTokens.flatMap((addr) => [
        { address: addr, abi: ERC20_SYMBOL_DECIMALS_ABI, functionName: "symbol" },
        { address: addr, abi: ERC20_SYMBOL_DECIMALS_ABI, functionName: "decimals" }
      ]),
      allowFailure: true
    }),
    factoryAddress !== zero ? publicClient.multicall({
      contracts: uniquePairKeys.map((p) => ({
        address: factoryAddress,
        abi: UNISWAP_V3_FACTORY_ABI,
        functionName: "getPool",
        args: [p.token0, p.token1, p.fee]
      })),
      allowFailure: true
    }) : Promise.resolve([])
  ]);
  const tokenMeta = {};
  for (let i = 0; i < uniqueTokens.length; i++) {
    const s = tokenMetaResults[i * 2];
    const d = tokenMetaResults[i * 2 + 1];
    tokenMeta[uniqueTokens[i]] = {
      symbol: s.status === "success" ? s.result : "???",
      decimals: d.status === "success" ? Number(d.result) : 18
    };
  }
  const poolAddressMap = {};
  for (let i = 0; i < uniquePairKeys.length; i++) {
    const key = `${uniquePairKeys[i].token0.toLowerCase()}-${uniquePairKeys[i].token1.toLowerCase()}-${uniquePairKeys[i].fee}`;
    const r = poolAddrResults[i];
    const addr = r?.status === "success" ? r.result : null;
    poolAddressMap[key] = addr && addr !== zero ? addr : null;
  }
  const poolAddresses = [
    ...new Set(Object.values(poolAddressMap).filter((a) => a !== null))
  ];
  const tickMap = {};
  if (poolAddresses.length > 0) {
    const slot0Results = await publicClient.multicall({
      contracts: poolAddresses.map((addr) => ({
        address: addr,
        abi: UNISWAP_V3_POOL_ABI,
        functionName: "slot0"
      })),
      allowFailure: true
    });
    for (let i = 0; i < poolAddresses.length; i++) {
      const r = slot0Results[i];
      if (r.status === "success") {
        const slot0 = r.result;
        tickMap[poolAddresses[i].toLowerCase()] = Number(slot0[1]);
      }
    }
  }
  return rawPositions.map((p) => {
    const t0 = tokenMeta[p.token0.toLowerCase()] ?? { symbol: "???", decimals: 18 };
    const t1 = tokenMeta[p.token1.toLowerCase()] ?? { symbol: "???", decimals: 18 };
    const pairKey = `${p.token0.toLowerCase()}-${p.token1.toLowerCase()}-${p.fee}`;
    const poolAddress = poolAddressMap[pairKey] ?? null;
    const currentTick = poolAddress ? tickMap[poolAddress.toLowerCase()] ?? null : null;
    const isClosed = p.liquidity === "0";
    const inRange = currentTick !== null && !isClosed ? currentTick >= p.tickLower && currentTick <= p.tickUpper : null;
    return {
      tokenId: p.tokenId,
      token0: p.token0,
      token1: p.token1,
      token0Symbol: t0.symbol,
      token1Symbol: t1.symbol,
      token0Decimals: t0.decimals,
      token1Decimals: t1.decimals,
      fee: p.fee,
      tickLower: p.tickLower,
      tickUpper: p.tickUpper,
      liquidity: p.liquidity,
      tokensOwed0: fromWei3(p.tokensOwed0Raw, t0.decimals),
      tokensOwed1: fromWei3(p.tokensOwed1Raw, t1.decimals),
      poolAddress,
      currentTick,
      inRange,
      isClosed
    };
  });
}
function createThemaWalletClient(config) {
  const { chain } = getChainConfig(config.chainId);
  return createPublicClient({ chain, transport: http(config.rpcUrl) });
}
async function getSafePortfolioWithPrices(config, publicClient) {
  const client = publicClient ?? createThemaWalletClient(config);
  const { safeAddress, tokens, nativeOracleAddress, nativeOracleDecimals } = config;
  const balanceCalls = tokens.map((t) => ({
    address: t.address,
    abi: ERC20_BALANCE_ABI,
    functionName: "balanceOf",
    args: [safeAddress]
  }));
  const oracleCalls = tokens.map((t) => ({
    address: t.oracleAddress,
    abi: CHAINLINK_AGGREGATOR_ABI,
    functionName: "latestRoundData"
  }));
  const nativeOracleCall = {
    address: nativeOracleAddress,
    abi: CHAINLINK_AGGREGATOR_ABI,
    functionName: "latestRoundData"
  };
  const [nativeBalance, results] = await Promise.all([
    client.getBalance({ address: safeAddress }),
    client.multicall({
      contracts: [...balanceCalls, ...oracleCalls, nativeOracleCall],
      allowFailure: true
    })
  ]);
  const n = tokens.length;
  const balanceResults = results.slice(0, n);
  const oracleResults = results.slice(n, 2 * n);
  const nativeOracleResult = results[2 * n];
  const now = Math.floor(Date.now() / 1e3);
  const tokenBalances = tokens.map((token, i) => {
    const rawBalance = balanceResults[i].status === "success" ? balanceResults[i].result : 0n;
    const balance = fromWei3(rawBalance, token.decimals);
    let priceUsd = "0";
    let priceAge = 0;
    if (oracleResults[i].status === "success") {
      const [, answer, , updatedAt] = oracleResults[i].result;
      if (answer > 0n) {
        priceUsd = fromWei3(answer < 0n ? 0n : answer, token.oracleDecimals);
        priceAge = now - Number(updatedAt);
      }
    }
    return {
      address: token.address,
      symbol: token.symbol,
      decimals: token.decimals,
      balance,
      balanceRaw: rawBalance.toString(),
      priceUsd,
      valueUsd: mulStrings(balance, priceUsd),
      priceAge
    };
  });
  let nativePriceUsd = "0";
  if (nativeOracleResult.status === "success") {
    const [, answer] = nativeOracleResult.result;
    if (answer > 0n) {
      nativePriceUsd = fromWei3(answer, nativeOracleDecimals);
    }
  }
  const nativeBalanceStr = fromWei3(nativeBalance, 18);
  const nativeValueUsd = mulStrings(nativeBalanceStr, nativePriceUsd);
  const totalValueUsd = sumUsd([nativeValueUsd, ...tokenBalances.map((t) => t.valueUsd)]);
  return { nativeBalance: nativeBalanceStr, nativePriceUsd, nativeValueUsd, tokens: tokenBalances, totalValueUsd };
}
async function getThemaWalletState(walletModuleAddress, publicClient, capitalDecimals = 6) {
  const a = walletModuleAddress;
  const results = await publicClient.multicall({
    contracts: [
      { address: a, abi: THEMA_WALLET_ABI, functionName: "safe" },
      { address: a, abi: THEMA_WALLET_ABI, functionName: "paused" },
      { address: a, abi: THEMA_WALLET_ABI, functionName: "isModuleEnabled" },
      { address: a, abi: THEMA_WALLET_ABI, functionName: "quoteSigner" },
      { address: a, abi: THEMA_WALLET_ABI, functionName: "vaultAccounting" },
      { address: a, abi: THEMA_WALLET_ABI, functionName: "activePositionCount" },
      { address: a, abi: THEMA_WALLET_ABI, functionName: "getActivePositionIds" },
      { address: a, abi: THEMA_WALLET_ABI, functionName: "totalExternalCapital" },
      { address: a, abi: THEMA_WALLET_ABI, functionName: "operatorCapitalBase" },
      { address: a, abi: THEMA_WALLET_ABI, functionName: "oracle" },
      { address: a, abi: THEMA_WALLET_ABI, functionName: "tvlPositionManager" },
      { address: a, abi: THEMA_WALLET_ABI, functionName: "v3Factory" },
      { address: a, abi: THEMA_WALLET_ABI, functionName: "tvlTwapWindow" }
    ],
    allowFailure: true
  });
  const [
    rSafe,
    rPaused,
    rModuleEnabled,
    rQuoteSigner,
    rVaultAccounting,
    rPositionCount,
    rPositionIds,
    rTotalExtCapital,
    rOperatorBase,
    rOracle,
    rPm,
    rFactory,
    rTwap
  ] = results;
  const zero = "0x0000000000000000000000000000000000000000";
  const rawIds = rPositionIds.status === "success" ? rPositionIds.result : [];
  return {
    safe: rSafe.status === "success" ? rSafe.result : zero,
    paused: rPaused.status === "success" ? rPaused.result : false,
    isModuleEnabled: rModuleEnabled.status === "success" ? rModuleEnabled.result : false,
    quoteSigner: rQuoteSigner.status === "success" ? rQuoteSigner.result : zero,
    vaultAccounting: rVaultAccounting.status === "success" ? rVaultAccounting.result : zero,
    activePositionCount: Number(rPositionCount.status === "success" ? rPositionCount.result : 0n),
    activePositionIds: rawIds.map((id) => id.toString()),
    totalExternalCapital: fromWei3(rTotalExtCapital.status === "success" ? rTotalExtCapital.result : 0n, capitalDecimals),
    operatorCapitalBase: fromWei3(rOperatorBase.status === "success" ? rOperatorBase.result : 0n, capitalDecimals),
    oracle: rOracle.status === "success" ? rOracle.result : zero,
    tvlPositionManager: rPm.status === "success" ? rPm.result : zero,
    v3Factory: rFactory.status === "success" ? rFactory.result : zero,
    tvlTwapWindow: Number(rTwap.status === "success" ? rTwap.result : 0)
  };
}
async function getSafeTVLData(walletModuleAddress, vaultAddress, stablecoin, publicClient, assetDecimals = 6) {
  const results = await publicClient.multicall({
    contracts: [
      {
        address: walletModuleAddress,
        abi: THEMA_WALLET_ABI,
        functionName: "computeSafeTVL",
        args: [vaultAddress, stablecoin]
      },
      {
        address: walletModuleAddress,
        abi: THEMA_WALLET_ABI,
        functionName: "vaultCapitalDeposited",
        args: [vaultAddress]
      },
      {
        address: walletModuleAddress,
        abi: THEMA_WALLET_ABI,
        functionName: "vaultTotalOut",
        args: [vaultAddress]
      },
      {
        address: walletModuleAddress,
        abi: THEMA_WALLET_ABI,
        functionName: "allowedVaults",
        args: [vaultAddress]
      }
    ],
    allowFailure: true
  });
  const [rTVL, rCapital, rTotalOut, rAllowed] = results;
  let spotTVL = "0";
  let twapTVL = "0";
  if (rTVL.status === "success") {
    const [spot, twap] = rTVL.result;
    spotTVL = fromWei3(spot, assetDecimals);
    twapTVL = fromWei3(twap, assetDecimals);
  }
  return {
    spotTVL,
    twapTVL,
    vaultCapitalDeposited: fromWei3(rCapital.status === "success" ? rCapital.result : 0n, assetDecimals),
    vaultTotalOut: fromWei3(rTotalOut.status === "success" ? rTotalOut.result : 0n, assetDecimals),
    vaultAllowed: rAllowed.status === "success" ? rAllowed.result : false
  };
}

export { ACKNOWLEDGEMENT_STATEMENTS, ADMIN_ROLE, BrowserThemaClient, CHAINS, CHAIN_CONFIGS, ChainType, DEFAULT_ADMIN_ROLE, EnvironmentType, FeeModelType, FeeReceiverRole, OracleType, SAFE_EXEC_ABI, THEMA_GUARD_ABI, THEMA_SAFE_CONFIGS, THEMA_WALLET_ABI, TOKEN_CONFIG, TokenInterface, TransactionType, TransferType, VAULT_ABI2 as VAULT_ABI, VAULT_CONFIGS, WebClient, artifacts2 as artifacts, buildClaimRedeemTx, buildClearWalletCapTx, buildDepositTx, buildFinalizeEpochTx, buildGrantRoleTx, buildPauseTx, buildPrepareFinalizeEpochTx, buildRecordConsentTx, buildRequestRedeemTx, buildRevokeRoleTx, buildSafeTxTypedData, buildSetConsentAcknowledgementsHashTx, buildSetDefaultWalletCapTx, buildSetDepositCapTx, buildSetSettlerTx, buildSetWalletCapTx, buildSetWalletCapsTx, buildSettleEpochTx, buildSweepExcessTx, buildUnpauseTx, computeAcknowledgementsHash, confirmSafeTransaction, createBrowserClient, createChainId, createThemaWalletClient, createWebClient, getActivePositionDetails, getBlockExplorerUrl, getChainConfig, getChainId, getChainName, getChainsByEnvironment, getConsentAcknowledgementsHash, getDefaultRpc, getDefaultRpcUrl, getDepositorsPositions, getEpochInfo, getOwnerSafes, getRedeemRequest, getSafeModulesAndGuard, getSafeNativeBalance, getSafeOnChainState, getSafePendingTransactions, getSafePortfolio, getSafePortfolioWithPrices, getSafeServiceInfo, getSafeServiceUrl, getSafeTVLData, getSafeTokenBalances, getSafeTransaction, getSafeTransactionHistory, getThemaGuardState, getThemaSafe, getThemaSafeByAddress, getThemaWalletAllowlists, getThemaWalletState, getTokenAddress, getTokenInterface, getUniswapConfig, getUserVaultPosition, getVaultAccessControl, getVaultAdminGrants, getVaultAssetMetadata, getVaultByAddress, getVaultForOmt, getVaultMast, getVaultState, getVaultWhitelist, getWalletCapStatus, hasCurrentConsent, isSafeServiceSupported, isSupportedChain, parseAndBuildDeposit, parseAndBuildRequestRedeem, proposeSafeTransaction, toAddress, validateAddress };
//# sourceMappingURL=ui.mjs.map
//# sourceMappingURL=ui.mjs.map